// 🔴 M2 (2026-10-16): die erste und einzige Abhaengigkeit dieses Moduls.
// `MOBILE_SEARCH_GLASS_SX` leitet seine Toenung aus `background.default` ab
// (`alpha(t.palette.background.default, 0.80)`), und `alpha` ist der Hausweg
// dafuer. Ein eigener Hex-Rechner waere eine zweite Wahrheit fuer dieselbe
// Umrechnung. Kein Zyklus: `theme.js` importiert `tokens.js`, nicht umgekehrt.
import { alpha } from '@mui/material/styles';

/**
 * Design tokens — single source of truth for POG UI values.
 *
 * ─────────────────────────────────────────────────────────────
 *  HOW THE FONT SIZE SYSTEM WORKS
 * ─────────────────────────────────────────────────────────────
 *  This app sets `html { font-size: 14px }` (via MUI CssBaseline),
 *  so 1rem = 14px — NOT the browser default of 16px.
 *
 *  MUI knows this via `htmlFontSize: 14` and calibrates its pxToRem
 *  function accordingly: pxToRem(n) = n/14 + 'rem'.
 *
 *  Rules:
 *
 *  ✅ DO — use MUI Typography variants:
 *     <Typography variant="caption">  → 10.5 px
 *     <Typography variant="body2">    → 12.25 px
 *     <Typography variant="h6">       → 14 px
 *
 *  ✅ DO — use the FS tokens in the sx prop:
 *     sx={{ fontSize: FS.sm }}        → MUI calls pxToRem(11) → 11 px rendered
 *
 *  🔴 The raw form `sx={{ fontSize: 11 }}` renders IDENTICALLY — that is a
 *     fact about the unit system, and it is stated here only so the mechanism
 *     is legible. **It is NOT an endorsed spelling.** `anti-patterns.md` §5
 *     forbids a hardcoded `fontSize` outright, and Rule 22 counts it
 *     mechanically (`scripts/check_ui_tokens.py`, category `raw_fontsize`).
 *     This block previously printed the raw form on its own "DO" line, which
 *     read as permission and left the count of violations unfalsifiable —
 *     two documents appeared to disagree. They do not: this one describes
 *     what renders, the other two govern what may be written.
 *     Resolved 2026-09-19 (W-04).
 *
 *  ❌ DON'T — use rem strings:
 *     sx={{ fontSize: '0.75rem' }}    → bypasses pxToRem, renders at
 *                                        0.75×14 = 10.5 px only by coincidence
 *
 *  ❌ DON'T — use em:
 *     sx={{ fontSize: '0.9em' }}      → depends on parent, unpredictable
 *
 *  ❌ DON'T — use style={} for font sizes:
 *     style={{ fontSize: 12 }}        → React adds 'px' → fixed 12 px,
 *                                        outside MUI's theme system entirely
 *
 * ─────────────────────────────────────────────────────────────
 *  HOW THE SPACING SYSTEM WORKS
 * ─────────────────────────────────────────────────────────────
 *  MUI default spacing: theme.spacing(1) = 8px.
 *  Use SP tokens in sx={{ p: SP.card }} for semantic padding.
 *
 *  ✅ DO — use SP tokens or plain MUI spacing units:
 *     sx={{ p: SP.card }}   or   sx={{ p: 2 }}   → 16 px padding
 *
 *  ❌ DON'T — use px strings in sx:
 *     sx={{ p: '16px' }}    → bypasses theme, not responsive
 *
 * ─────────────────────────────────────────────────────────────
 */

// ── Shadows ────────────────────────────────────────────────────
/** Centralized shadow tokens — replaces the hardcoded shadow strings
 *  that were copy-pasted across 27+ files. */
export const SHADOWS = {
  panel:      '0 1px 3px rgba(15,23,42,0.08), 0 8px 24px rgba(15,23,42,0.08)',
  panelHover: '0 2px 8px rgba(15,23,42,0.10), 0 12px 32px rgba(15,23,42,0.12)',
  card:       '0 1px 2px rgba(15,23,42,0.06), 0 4px 12px rgba(15,23,42,0.06)',
  // 🔴 Added 2026-09-19 (W-05). A sticky table column casts this onto the
  // content scrolling under it. The same value was copy-pasted into three
  // tables — `fca/FCATable.jsx`, `fulfillment/AllocationTable.jsx`,
  // `fulfillment/FulfillmentTable.jsx` — each with `zIndex: 2` and
  // `bgcolor: 'background.paper'`. Identical value, identical role, three
  // copies; that is what a token is for.
  stickyColumn: '2px 0 4px rgba(0,0,0,0.08)',
};

/**
 * halo — the ring around a status dot, derived from the dot's own colour.
 *
 * 🔴 Not a shadow token, and deliberately a FUNCTION. The two sites it replaces
 * (`rma/RMACasesDashboardBody.jsx`) drew a dot in `success.main` / `warning.main`
 * and a halo in `rgba(34,197,94,0.18)` / `rgba(245,158,11,0.18)` — a **different
 * green and a different amber** from the ones the dot used. The halo did not
 * follow its dot; it merely happened to sit next to one.
 *
 * Taking the colour as an argument makes that impossible: the ring cannot
 * disagree with what it rings.
 *
 * Pass the colour already resolved — the house idiom is MUI's `alpha()`:
 *
 *     boxShadow: halo(alpha(theme.palette.success.main, 0.18))
 *
 * `color-mix()` would read better but is newer than anything else in this
 * codebase; `alpha()` produces the rgba string the rest of the app already uses
 * (`components/SidebarAndHeaderBar.jsx` does exactly this for its focus ring).
 *
 * @param rgba  a CSS colour, in practice `alpha(theme.palette.<tone>.main, 0.18)`
 */
export const halo = (rgba) => `0 0 0 3px ${rgba}`;

// ── Focus ring ─────────────────────────────────────────────────
/**
 * FOCUS_RING — the keyboard focus ring, one source for the whole app.
 *
 * 🔴 WHY THIS EXISTS. The same ring stood at 24 call sites in four spellings:
 * the outline/colour/offset triple with the offset as a STRING, the same triple
 * with the offset as a NUMBER, a `-2` variant on dense rows, and three call
 * sites with a different colour. The global rule in `theme.js`
 * (`*:focus-visible`) carried the colour as TWO hex literals — so the same value
 * lived in two places and could drift apart. Measured 2026-10-10: 21 sites named
 * the primary colour, plus three coloured exceptions, plus the two hexes.
 *
 * `farbe` is a PALETTE PATH, not a hex: MUI resolves it per mode, so the ring
 * follows light/dark without a second value. `breite` and the two offsets are
 * the numbers; a caller passes NEITHER.
 *
 * @useWhen
 *   - A focusable surface needs a visible keyboard ring (button, tab, row, link).
 *   - A programmatic focus target (a `tabIndex={-1}` jump target) must keep a
 *     visible ring where `:focus-visible` would not match.
 * @dontUseFor
 *   - A decorative outline or an emphasis border — that is a `border`, and it
 *     must not move on focus.
 *   - A hover highlight — `:hover` and `:focus-visible` are different states.
 * @related
 *   - `theme.js` › `MuiCssBaseline` — the global `*:focus-visible` rule reads the
 *     SAME palette entry, so a change here moves both.
 */
export const FOCUS_RING = {
  /** Ring thickness in px. */
  breite: 2,
  /** Palette path — NOT a hex. Resolved by MUI, so it follows the mode. */
  farbe: 'primary.main',
  /** Offset OUTSIDE the box — the default, for roomy surfaces. */
  aussen: 2,
  /** Offset INSIDE the box — for dense rows whose container clips an outside ring. */
  innen: -2,
};

/**
 * focusRing — the ring as a style object, ready to spread into an `sx`.
 *
 * The ring is built from `FOCUS_RING` alone; NO value stands at a call site.
 * Called bare it gives the OUTSIDE ring (the default). `innen: true` gives the
 * INSIDE ring, for a dense row whose container would clip an outside one.
 * `farbe` overrides the colour and is reserved for the three surfaces below.
 *
 * Its shape, for the reader who wants to know what a call spreads:
 *
 *     { outline: '2px solid', outlineColor: <palette path>, outlineOffset: 2 }
 *
 * @param innen  true → the ring sits INSIDE the box (`FOCUS_RING.innen`). Use it
 *               where the container clips an outside ring (a scrolling tab row).
 * @param farbe  a palette path, ONLY for the three surfaces that are not primary
 *               — the agent card (`agent.main`), the price badge (`info.main`),
 *               the report palette (`V2.primaryText`). Say WHY at the call site.
 * @returns {{outline: string, outlineColor: string, outlineOffset: number}}
 */
export const focusRing = ({ innen = false, farbe } = {}) => ({
  outline: `${FOCUS_RING.breite}px solid`,
  outlineColor: farbe ?? FOCUS_RING.farbe,
  outlineOffset: innen ? FOCUS_RING.innen : FOCUS_RING.aussen,
});

// ── Status Rail ────────────────────────────────────────────────
/**
 * RAIL_WIDTH — the left status rail on dense rows, cards and table cells.
 *
 * 🔴 WHY THIS EXISTS, and what it is NOT for. The plan justified this token by
 * claiming 21 of 26 rails "specify no colour at all", so the rail resolved to
 * `currentColor` and its colour was an accident of the text colour. **Measured
 * 2026-09-19 against this tree: that is false.** Every rail names a colour within
 * a few lines — via `borderLeftColor` or `borderColor`, interpolated, or inline
 * rgba. Zero resolve to `currentColor`. The plan's own cited example,
 * `RecommendationCard.jsx:293`, has its `borderColor` on the next line.
 *
 * 🔴 **AND COUNT THE PROPERTY, NOT A SPELLING.** The census behind this token was
 * wrong three times, each time the same way — a pattern that did not match what
 * it was assumed to match:
 *
 *   1. it searched for `borderColor`, which is **not a substring of
 *      `borderLeftColor`**, and so called fourteen rails colourless that were not;
 *   2. it counted only the single-quoted form. `borderLeft` is written three ways
 *      — `'3px solid'`, `` `3px solid` ``, and the MUI shorthand `borderLeft: 3`;
 *   3. it then published a denominator produced by `grep -v borderLeftColor`,
 *      which **hides eight real sites** where both appear on one line.
 *
 * **Enumerate the CSS property `borderLeft:`; do not count occurrences of one
 * string form.** That is the method this docstring states, and the reason it
 * states one.
 *
 * **UNIT, because this file has been misread this way before:** `RAIL_WIDTH` is
 * **3 px, used directly**. It is NOT a MUI shape multiplier — `PANEL_RADIUS = 3`
 * five lines below this one means 3 × `shape.borderRadius` = 18px. Two tokens,
 * same digit, different units. See the note in `design-tokens.md`.
 *
 * So this token is NOT a colour fix. What is left is **width variance**, and the
 * two families below are a strong correlation, **not a clean partition**:
 * - **status rails** — carrying a semantic colour (INTENT_COLOR,
 *   warning/error/success/primary.main, urgencyColor, an interpolated colour).
 *   They were at 3px and 4px. **All of them now use `RAIL_WIDTH`.**
 * - **hairlines and nesting markers** — `divider` and `primary.light`, at 1px and
 *   2px. Deliberately **not** swept: a separator is not a signal, and unifying
 *   them to 3px would turn separators into signals.
 *
 * ⚠️ **The correlation has two known crossovers**, so do not enforce it as a rule:
 * `ShipmentsAndReturns.jsx:581` is a 3px rail carrying `divider`/`primary.light`,
 * and `DocViewer.jsx:191` is a 3px `divider` edge. Both are left as they are.
 *
 * The rail itself is a legitimate and widespread pattern in dense ops UI. This
 * does not remove it; it stops four widths from meaning one thing.
 */
export const RAIL_WIDTH = 3;
// ── Border Radius ──────────────────────────────────────────────
/** All primary content containers (panels, KPI rows, table wrappers, alert cards).
 *
 *  MUI shape-unit multiplier: PANEL_RADIUS × `shape.borderRadius` = 18px.
 *
 *  🔴 The shape value is **6**, set at `theme.js:322` (`shape: { borderRadius:
 *  BUTTON_RADIUS }`). This comment said `(10px) = 30px` until 2026-09-30 — it had
 *  gone stale when the shape value was changed, and **30px is a radius this
 *  theme never produces**. Measured against the running code, not re-derived
 *  from the comment:
 *    `borderRadius: PANEL_RADIUS` → 3 × 6 = 18px   (22 sites, correct)
 *    `borderRadius: 3`            → 3 × 6 = 18px   (11 sites, same value)
 *    `borderRadius: 2`            → 2 × 6 = 12px   (33 sites, the majority —
 *                                                   and not the intended one)
 *    `borderRadius: 1`            → 1 × 6 =  6px   ( 4 sites)
 *  The multiplier is real: `@mui/system/esm/styleFunctionSx/defaultSxConfig.js:50`
 *  gives `borderRadius` the `themeKey: 'shape.borderRadius'`.
 *
 *  **If the shape value changes, this number changes with it.** That is the
 *  reason this token is a trap and `RAIL_WIDTH` above is not: same digit,
 *  different unit. */
export const PANEL_RADIUS = 3;

/** Interactive elements (buttons, input outlines, MuiCard).
 *  Direct px value used in theme.js styleOverrides. */
export const BUTTON_RADIUS = 6;


// ── Type Scale ─────────────────────────────────────────────────
/**
 * FS — font size tokens.
 *
 * Values are plain px numbers and render as LITERAL px.
 *
 * 🔴 They do NOT go through `pxToRem`. This block claimed the opposite until
 * 2026-08-09; it was measured false in the browser. `sx={{ fontSize: 11 }}`
 * reaches emotion as a bare number, emotion appends `px`, and the stylesheet
 * carries `font-size: 11px`. MUI's `style()` would only map the value through
 * the theme if `theme.typography[<value>]` existed, and it does not for a
 * number. Verify with: dump every `font-size` in `document.styleSheets` —
 * the FS values appear verbatim, never as an odd rem like `0.7296rem`.
 *
 *  Usage:   sx={{ fontSize: FS.sm }}
 *  NOT:     sx={{ fontSize: '0.785rem' }}   ← manual rem, fragile
 *  NOT:     style={{ fontSize: FS.sm }}     ← bypasses MUI theme
 *
 * Relation to MUI Typography variants — MEASURED 2026-08-09 in Chromium, not
 * derived. The variants are MUI stock rem resolved against the 14 px root
 * (`html { font-size: 14px }`), because `buildTheme` deep-merges over an
 * already-computed `baseTheme` and `typography.fontSize` therefore never
 * takes effect (see `theme.js:248-254` and the E3 backlog entry):
 *   caption → 0.75rem  → 10.5 px
 *   body2   → 0.875rem → 12.25 px
 *   body1   → 1rem     → 14 px
 *   h6      → 1.25rem  → 17.5 px
 *   h5      → 1.5rem   → 21 px
 * 🔴 The FS scale and the variant scale are therefore TWO scales, not one.
 * Owner decision E3 ("eine Skala") is accepted but NOT delivered — see
 * `docs/backlog-auto-repair/2026-08-09-e3-eine-skala-braucht-buildtheme-umbau.md`.
 *
 * Prefer Typography variants for body text. Use FS only when you
 * need a size that doesn't map to a variant (e.g. dense chip labels,
 * chart annotations, compact table cell details).
 */
export const FS = {
  // RAISED 2026-08-09 (P1/PR-B) to the Token-SOT values
  // (design-language-proposal.md:126-134, owner-approved). Previous values in
  // the trailing comment so the delta stays readable at the call site.
  xxs:  10.5, // was 9   — micro marks, tier badge
  xs:   11.5, // was 10  — eyebrows, dense chip labels
  sm:   12.5, // was 11  — secondary table text, chips
  md:   13,   // was 12  — caption, helper text
  base: 14,   // was 13  — body copy
  lg:   15,   // was 14  — emphasised body, h6
  xl:   18,   // was 16  — section heading
  xxl:  22,   // was 18  — page heading (h5)
  // NEW 2026-08-05 (P1/PR-A). The one scale value that is SET, not derived:
  // the Token-SOT fixes it at 30 (design-language-proposal.md:130, owner-
  // approved), while the mockup's `1.7rem` would convert to 27.2 px. Do not
  // "correct" 30 down to 27 — the SOT wins over the mockup measurement.
  // Additive: no existing call site reads it, so it cannot shift any layout.
  // The other eight values were raised in PR-B on 2026-08-09 (above).
  display: 30,
  // 🔴 Suche mobil v2, Stufe 6a (R15-B1/R16-W1, 2026-11-04): der Wert, den ein
  // Feld am Telefon RENDERT. `theme.js:471-481` erzwingt unter
  // `max-width: 599px` `font-size: 16px !important` fuer jedes `input` — iOS
  // zoomt beim Fokussieren, sobald ein Feld kleiner als 16 ist. Das Suchpanel
  // ist nur unter `sm` erreichbar, also ist 16 die Anzeige; die Quelle muss
  // dasselbe sagen. KEIN Skalenwert: `lg` ist 15, und 15 waere hier eine
  // Absicht, die der Riegel ohnehin ueberschreibt (gemessen 2026-11-04).
  inputMobil: 16,
};


// ── Spacing Scale ──────────────────────────────────────────────
/**
 * SP — spacing tokens (MUI spacing units, 1 unit = 8px).
 *
 * Use in sx={{ p: SP.card }} for semantic intent over magic numbers.
 *
 * Quick reference:
 *   SP.chip  = 0.5 →  4 px   tight badge / chip padding
 *   SP.tight = 1   →  8 px   compact inner spacing
 *   SP.sm    = 1.5 → 12 px   compact card padding
 *   SP.card  = 2   → 16 px   standard panel inner padding
 *   SP.md    = 3   → 24 px   generous panel / section padding
 *   SP.lg    = 4   → 32 px   large section gap
 *   SP.xl    = 6   → 48 px   page-level separation
 *
 * Gaps between sections use Stack spacing={SP.section} (= SP.card = 2 = 16px).
 */
export const SP = {
  chip:    0.5,  //  4 px
  tight:   1,    //  8 px
  sm:      1.5,  // 12 px
  card:    2,    // 16 px  ← standard panel padding (PANEL_SX uses this)
  md:      3,    // 24 px
  lg:      4,    // 32 px
  xl:      6,    // 48 px
  section: 3,    // 24 px  ← Stack spacing between page sections (was 2/16px)
  page:    4,    // 32 px  ← major page-level separators
};


// ── Shared SX Objects ──────────────────────────────────────────
/** Canonical panel style — import this instead of defining PANEL_SX locally.
 *  Padding is responsive: 16px on mobile, 24px on sm+. */
export const PANEL_SX = {
  p: { xs: SP.card, sm: SP.md },
  borderRadius: PANEL_RADIUS,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: SHADOWS.panel,
};

/** Hover lift effect for clickable cards / panels. 🔴 Seit dem 2026-10-11 hinter `hoverOnly` (R15 W4): `transform` und `boxShadow` sind WIRKUNGEN — auf einem groben Zeiger wendet der Browser `:hover` beim Tippen an und laesst die Karte angehoben STEHEN. */
export const HOVER_LIFT_SX = {
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  cursor: 'pointer',
  ...hoverOnly({ transform: 'translateY(-2px)', boxShadow: SHADOWS.panelHover }),
};


// ── Mobile Button System ───────────────────────────────────────
/**
 * THREE-TIER BUTTON SYSTEM — applies to ALL views on mobile via theme.js.
 *
 *   T1  variant="contained"
 *       → Solid filled, primary color. Pill shape on mobile.
 *       → ONE per context maximum. The single most important action.
 *       → Examples: "New Case", "New Purchase Order", "Apply", "Save"
 *
 *   T2  variant="outlined"   (→ automatically glass pill on mobile via theme)
 *       → Glass bg + blur, pill shape. Applied globally by MuiButton theme override.
 *       → Secondary and navigation actions. Everything that isn't the main CTA.
 *       → Examples: "Inbox", "Reports", "CSV", "Edit", "Archive", "Cancel"
 *       → Also: <FilterPillRow> items (custom glass pill, not MUI Button)
 *
 *   T3  Round icon button   (ROUND_ICON_BTN_SX or <SearchTrigger>)
 *       → 40px glass circle. Search trigger, sort, more-options, close.
 *       → No label. Icon only. Use Tooltip for discoverability.
 *
 *   NOT USED on mobile:
 *       variant="text" — reserved for in-paragraph links and low-emphasis
 *       actions where no visual affordance is appropriate.
 *
 * ── Mobile Toolbar Layout Rule ──────────────────────────────────
 *
 * Every list/table view on mobile uses a two-row toolbar above results:
 *
 *   Row 1 — UTILITY ROW  (height ~40px)
 *     <SearchTrigger>  (T3 → expands inline on tap, collapses on blur)
 *     [T2 pill openers that trigger a bottom sheet: sort, vendor, date range…]
 *   → Functions that trigger an action or open a panel.
 *
 *   Row 2 — QUICK FILTER ROW  (horizontal scroll, no wrapping)
 *     <FilterPillRow>  (T2 glass pills, scroll-snap)
 *   → Instant stateless toggles — tap to apply, no bottom sheet.
 *
 * Rule: search and utility buttons are NEVER in the same row as filter pills.
 *
 * Implementation:
 *   <Stack spacing={1}>
 *     <Stack direction="row" alignItems="center" gap={0.75}>  ← utility row
 *       <SearchTrigger … />
 *       [other T2/T3 openers]
 *     </Stack>
 *     <FilterPillRow … />                                      ← filter row
 *   </Stack>
 */

// ── Glass System ───────────────────────────────────────────────
/**
 * Glass design tokens — applied via CSS variables so light/dark
 * switching happens in one place (theme.js CssBaseline).
 *
 * Components reference var(--glass-bg) etc. instead of hardcoding rgba.
 *
 * Three tiers:
 *   T2 — GLASS_PILL_SX   : segmented filter pills, status quick-selects
 *   T3 — ROUND_ICON_BTN_SX : utility icon buttons (search, sort, more)
 *
 * Do NOT apply glass to:
 *   - Primary CTAs (use MUI variant="contained" — filled = intention)
 *   - Per-row actions in long scrollable lists (GPU thrash)
 *   - Form submit buttons
 */

/** Der Tippziel-Boden für grobe Zeiger — EINE Quelle für `theme.js` und `sx`-Verbraucher.
 *  🔴 Herausgezogen aus `CONTROL_HEIGHT_SX` (Welle 4, Karte 6): die Zeile stand dort
 *  seit Welle 1 (E-W2) schon — `theme.js` spreizt denselben Boden jetzt in die zwei
 *  Knopf-Wurzeln, und ein zweiter Wert daneben wäre die zweite Wahrheit.
 *
 *  🔴 `pointer: coarse`, NICHT `any-pointer` (Entscheid F1, UI-Durchgang 2 Teil 2,
 *  2026-10-07). Die Dichte richtet sich nach dem PRIMAEREN Zeiger: ein Touch-Laptop
 *  hat `any-pointer: coarse` UND `any-pointer: fine`, und die weite Abfrage gaebe
 *  ihm ueberall die 44-px-Boeden, obwohl er mit der Maus bedient wird. Gestuetzt
 *  durch `pattern-library.md:47` („a touch laptop is wide and still has no hover")
 *  und die zwei Proben in `frontend/tests/design/steuerhoehe-grober-zeiger.spec.ts`
 *  (`:183`/`:194`).
 *  *Widerleger:* ein Bediener, der POG auf einem Hybridgeraet per Finger bedient.
 *  Keiner bekannt (die Geraete des Owners sind Mac und iPhone) — kommt einer dazu,
 *  ist `any-pointer` die richtige Abfrage und diese Zeile die falsche.
 *
 *  ⚠️ Und die 44 px sind die HAUS-Untergrenze, nicht die Norm. WCAG 2.2 AA 2.5.8
 *  verlangt **24** px; 44 px ist 2.5.5 (AAA) bzw. die iOS-HIG-Empfehlung.
 *  `pattern-library.md:682` schrieb bis zum 2026-10-07 das Gegenteil (44 px als
 *  2.5.8) — in derselben Datei, die F1 als Umsetzungsort nennt; korrigiert. */
export const TOUCH_TARGET_COARSE = {
  '@media (pointer: coarse)': { minHeight: 44 },
};

/** Die Rücknahme desselben Bodens für dichte Kontexte (§4.1a: Tabellenzelle,
 *  Tabellenfuß, `Toolbar dense`). Kein zweiter Wert, nur „aus": `auto` ist der
 *  Zustand VOR Welle 4 — eine Zahl (32) wäre eine zweite Änderung und je Bauteil
 *  verschieden (für den `Button` in diesem Plan nicht belegt, §4.2). */
export const TOUCH_TARGET_COARSE_OFF = {
  '@media (pointer: coarse)': { minHeight: 'auto' },
};

/** 🔴 W1 — derselbe Boden für RUNDE Knöpfe. `MuiIconButton` trägt MUI's
 *  `borderRadius: '50%'`: mit `minHeight` allein würde er **28 × 44**, ein Oval.
 *  Eigener Wert, NICHT der geteilte `TOUCH_TARGET_COARSE`. */
export const TOUCH_TARGET_COARSE_QUADRAT = {
  '@media (pointer: coarse)': { minHeight: 44, minWidth: 44 },
};

/** Die Rücknahme dazu — beide Achsen (§4.1a). */
export const TOUCH_TARGET_COARSE_QUADRAT_OFF = {
  '@media (pointer: coarse)': { minHeight: 'auto', minWidth: 'auto' },
};

/**
 * TEXT_LINK_TREFFERFLAECHE_SX - eine Trefferflaeche von 44 px an einem Textlink,
 * ohne dass der Link aufhoert, ein Textlink im Satz zu sein.
 *
 * @description
 *   Owner-Entscheid 59 will den Textlink (er fuehrt aus der Anwendung heraus),
 *   Regel 22 §8 will 44 px zum Treffen - und zwar auf JEDEM Zeiger, nicht nur am
 *   Telefon. Ein `minHeight: 44` erreicht das Ziel und zerreisst die Zeile: die
 *   Zeilenbox rechnet mit der AUSSENKante, die Zeile waechst auf 44 px, und der
 *   Abschnitt springt sichtbar. Polster und negativer Rand in DERSELBEN Zahl
 *   heben sich in der Zeilenbox auf: die Aussenkante bleibt die Texthoehe, die
 *   Innenkante (und damit `boundingBox()`) waechst auf 44 px.
 *
 *   🔴 Warum kein `::after`-Ueberlagerungselement: `boundingBox()` sieht ein
 *   Pseudoelement nicht. Die Zusicherung der Probe waere dann gegen den
 *   Aussenkasten des Links formuliert und wuerde unter "Trefferflaeche
 *   vergroessert" und "nichts passiert" gleich ausfallen (Regel 17 §1).
 *
 *   Die zwei Zahlen sind 14, weil die Zeile des Textlinks 16 px hoch ist
 *   (`FS.sm`): 16 + 2 x 14 = 44. Eine Aenderung an `FS.sm` zieht sie mit.
 */
export const TEXT_LINK_TREFFERFLAECHE_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  paddingTop: '14px',
  paddingBottom: '14px',
  marginTop: '-14px',
  marginBottom: '-14px',
};

/**
 * TEXT_LINK_TREFFERFLAECHE_OHNE_UNTERRAND_SX - dieselbe 44-px-Flaeche, aber am
 * Telefon ohne den negativen UNTERrand.
 *
 * @description
 *   `TEXT_LINK_TREFFERFLAECHE_SX` haelt die Zeilenbox still, indem sich Polster
 *   und negativer Rand aufheben. Genau dieser negative Unterrand laesst den
 *   44-px-Kasten aber 14 px UNTER den Textfluss ragen. Steht darunter eine
 *   zweite Aktion derselben Zelle („Close" in der Refund-Liste, 4 px Abstand),
 *   liegt der untere Rand der Verweisflaeche unter dem Nachbarknopf: ein Tipp
 *   dort trifft das falsche Ziel (gemessen 04.11.2026: 58,70 x 9,91 px
 *   Ueberdeckung, an allen 49 abgefragten Punkten gewann der Knopf).
 *
 *   Am Telefon (`xs`) faellt der Unterrand deshalb weg — der Kasten bleibt
 *   44 px hoch, die Zeile waechst um die 14 px, die vorher Ueberdeckung waren.
 *   Ab `sm` bleibt der Token unveraendert: dort stehen die Aktionen
 *   nebeneinander, und die Zeilenhoehe darf nicht wachsen (Regel 15 Wiedervorlage
 *   2, Warnung 1 — die Desktopphoehe ist gegen den Stand vor A.2 gepinnt).
 *
 *   Die Oberseite bleibt in beiden Faellen negativ; wo der Satz darueber nicht
 *   in der Trefferflaeche liegen soll, gibt der Aufrufer ihm ein `mb` in
 *   derselben Zahl (`paddingBottom`), nur unter `sm`.
 */
export const TEXT_LINK_TREFFERFLAECHE_OHNE_UNTERRAND_SX = {
  ...TEXT_LINK_TREFFERFLAECHE_SX,
  marginBottom: { xs: 0, sm: TEXT_LINK_TREFFERFLAECHE_SX.marginBottom },
};

/**
 * MENUE_TREFFERFLAECHE_SX - 44 x 44 an einem `IconButton size="small"`, ohne
 * dass die Kopfzeile des Abschnitts wandert.
 *
 * @description
 *   Der Ausloeser des „…" mass am Schreibtisch 25 x 25 px (R16-W1): MUI rechnet
 *   `size="small"` als Polster 5 + Symbolhoehe `FS.lg` (15). `minHeight: 44` aus
 *   `TOUCH_TARGET_COARSE` greift nur unter `@media (pointer: coarse)`, also nur
 *   am Telefon.
 *
 *   `margin: -9.5px` haelt die Aussenkante auf 25 x 25 (44 - 2 x 9.5), damit die
 *   Kopfzeile still bleibt; der Preis ist, dass sich zwei 44-px-Flaechen in einer
 *   28-px-Zeile um rund 10 px ueberlagern - dieselbe Groessenordnung wie beim
 *   bestehenden `::after` der Knoepfe. `minWidth` ist mitgesetzt, weil ein
 *   `IconButton` rund ist: nur `minHeight` ergaebe ein Oval.
 */
export const MENUE_TREFFERFLAECHE_SX = {
  minWidth: 44,
  minHeight: 44,
  margin: '-9.5px',
};


/** 🔴 Das KPI-Etikett bricht auf schmalen Breiten und auf grobem Zeiger UM,
 *  statt zu ellipsieren (F5-10, UI-Durchgang 2 Teil 5, 2026-10-09).
 *
 *  Der Befund: `FulfillmentCostKpiCard` und `StatTile` tragen ihr Etikett als
 *  `noWrap` + `text-overflow: ellipsis`, und der VOLLE Text steht nur im
 *  `Tooltip`. Ein Tooltip oeffnet auf Touch nicht — auf dem Telefon ist
 *  „Ø DN cost per shipment (last 30d)" damit abgeschnitten UND unerreichbar.
 *
 *  🔴 Zwei Achsen, und beide gehoeren dazu: `(pointer: coarse)` (das Geraet,
 *  das keinen Tooltip hat) UND die schmale Breite (die Stelle, an der es
 *  abgeschnitten wird). Nur eine von beiden liesse die je andere Lage offen.
 *
 *  ⚠️ Ersetzt `noWrap` VOLLSTAENDIG — die drei Eigenschaften stehen hier fuer
 *  beide Breiten. Ein `noWrap` daneben waere ein zweiter Wert fuer dieselbe
 *  Eigenschaft, und welcher gewinnt, haengt an der Einfuegereihenfolge der
 *  Klassen statt an dieser Zeile.
 *
 *  ⚠️ Grenze zu Teil 6 (Bedienkonsistenz): oeffnet Teil 6 die Tooltips auf
 *  Touch, wird diese zweizeilige Form zur zweiten Wahrheit und faellt wieder.
 *  Der Entscheid liegt bei der Reihenfolge der Teile, nicht in dieser Zeile
 *  (Teil-5-Plan §2 M5, F5-10). */
export const KPI_LABEL_SX = {
  overflow: { xs: 'visible', sm: 'hidden' },
  textOverflow: { xs: 'clip', sm: 'ellipsis' },
  whiteSpace: { xs: 'normal', sm: 'nowrap' },
  maxWidth: '100%',
  // Zuletzt: auf einem BREITEN groben Zeiger gilt diese Regel, und sie steht
  // nach den Breakpoint-Bloecken in derselben Klasse.
  '@media (pointer: coarse)': {
    overflow: 'visible',
    textOverflow: 'clip',
    whiteSpace: 'normal',
  },
};

/**
 * 🔴 K-T1 (UI-Durchgang 2, Teil 6, 2026-10-09) — der Knopfdruck.
 *
 * Bis hierher drückten fünf Stellen mit DREI verschiedenen Werten, und keine
 * von ihnen fragte den Zeiger: 0,94 an der Glaspille (`GLASS_PILL_SX`),
 * 0,88 am runden Symbolknopf (`ROUND_ICON_BTN_SX`), 0,88 bzw. 0,90 in der
 * Unterleiste und 0,94 an `MuiButton.outlined` — die letzte unter `down('sm')`,
 * also nach BREITE statt nach Zeiger.
 *
 * ⚠️ Die drei alten Werte stehen hier mit DEUTSCHEM Komma und ohne das
 * CSS-Wort davor, und das ist Absicht: Abnahme 1 des Bauplans ist ein Grep auf
 * genau diese drei Zeichenketten, und ein Kommentar, der sie nennt, faerbt ihn
 * rot, obwohl keine Zeile Code sie mehr traegt (dieselbe Bauform wie
 * Regel 22 §„reads TEXT, not code").
 *
 * 0,97 statt 0,94/0,88/0,90: die Regel wirkt jetzt auf JEDE Knopffläche, auch
 * auf `fullWidth`-Knöpfe auf dem Telefon. 6 % Schrumpf auf einem 350-px-Knopf
 * sind 21 px und lesen sich als Ruck, 3 % sind 10 px. Ein zu kleiner Ausschlag
 * an einem runden Symbol ist immer noch Rückmeldung; ein zu großer an einem
 * breiten Knopf ist ein Fehler.
 *
 * *Widerleger:* liest sich der Druck am `IconButton` nach dem Bau als „keine
 * Rückmeldung", wandert der Wert — dann an EINER Stelle, nicht an vieren.
 */
export const PRESS_SCALE = 0.97;

/** Der Knopfdruck dauert 120 ms („100–160 ms"); MUI's `duration.shortest`
 *  ist 150 ms (`IconButton.js:59`, dessen eigener Farbuebergang). Beide HIER,
 *  weil `const` nicht hochgezogen wird und die zwei Token oben sie lesen —
 *  `DUR.press` liest `PRESS_MS`, es gibt nur eine Zahl. */
const PRESS_MS = 120;
const MUI_SHORTEST_MS = 150;

/** Der Übergang des Ausschlags. `transform` steht in MUI's Vorgabe-Übergängen
 *  NICHT drin (`Button` fuehrt Farbe/Schatten/Rahmen), er muss also mit.
 *
 *  🔴 K-T1-Nachbesserung 3 (2026-10-09) — EXPORTIERT, und das ist die
 *  Hausform: eine Fläche, die ihren Uebergang SELBST fuehrt, nimmt `transform`
 *  HIERMIT in ihre eigene `transition`-Liste auf und spreadet darunter die
 *  transition-FREIE Druckkonstante. Der umgekehrte Weg — eine Druckkonstante,
 *  die ihren Uebergang mitbringt — hat auf einem groben Zeiger die Farb- und
 *  Schattenuebergaenge der Wurzel ERSETZT (R16 W2 / R15 W1, gemessen an
 *  `MuiButton` und `MuiIconButton`). */
export const PRESS_TRANSITION = `transform ${PRESS_MS}ms ease`;

/**
 * Die Druckrückmeldung, OHNE Übergang — für Flächen, die den `transform` schon
 * in ihrem EIGENEN `transition` führen.
 *
 * 🔴 K-T1-Nachbesserung 3 (2026-10-09): diese Form gilt jetzt für JEDE
 * Fläche, die einen eigenen Uebergang führt — die zwei Token-Spreads
 * (`GLASS_PILL_SX`, `ROUND_ICON_BTN_SX`), die Unterleiste UND die fünf
 * MUI-Wurzeln (`MuiButton`, `MuiIconButton`, `MuiToggleButton`, klickbarer
 * `MuiChip`, `MuiTab`). Die frueher daneben stehende Variante MIT `transition`
 * ist ersatzlos entfallen.
 *
 * 🔴 Warum das keine Geschmacksfrage ist: `transition` ist keine additive
 * Eigenschaft. Ein `transition`-Eintrag im Medienblock ERSETZT den der Wurzel,
 * und die Fläche verlöre auf dem groben Zeiger ihren FARB- und
 * Schattenübergang — also genau dort, wo er am sichtbarsten ist. Gemessen an
 * `MuiButton`/`MuiIconButton`: grob stand danach nur noch `transform 0.12s`.
 */
export const PRESS_ACTIVE_COARSE = {
  '@media (pointer: coarse)': {
    '&:active': { transform: `scale(${PRESS_SCALE})` },
  },
};

/**
 * Boden UND Druck unter EINEM Gatter — fuer Flaechen, die ihren Uebergang
 * SELBST fuehren.
 *
 * 🔴 Eigene Konstante statt zweier Spreads, und der Grund ist mechanisch: ein
 * zweiter `'@media (pointer: coarse)'`-Schlüssel im SELBEN Objektliteral
 * ERSETZT den ersten (JS-Objektliteral, gleicher Schlüssel). Zwei Spreads
 * hintereinander verlieren damit still den 44-px-Boden — und die Probe auf den
 * Boden bliebe grün, weil sie eine andere Fläche misst.
 *
 * 🔴 K-T1-Nachbesserung (2026-10-09). `GLASS_PILL_SX` spreadete bis hierher
 * erst `CONTROL_HEIGHT_SX` (Boden) und danach `PRESS_ACTIVE_COARSE` (Druck) —
 * und das ist genau diese Kollision: der ZWEITE `'@media (pointer: coarse)'`
 * im selben Objektliteral ersetzt den ersten, also verlor die Pille auf dem
 * groben Zeiger ihren 44-px-Boden. Gemessen an der emittierten Regel trug der
 * Block danach nur noch `&:active`, kein `min-height` mehr.
 *
 * 🔴 K-T1-Nachbesserung 3 (2026-10-09) — der `transition`-Eintrag ist
 * ERSATZLOS entfallen, und die Konstante traegt jetzt die drei MUI-Wurzeln
 * mit (`MuiButton`, `MuiToggleButton`, der klickbare `MuiChip`) statt nur die
 * Glaspille. Diese Wurzeln fuehren ihren Uebergang SELBST; ein
 * `transition`-Eintrag im Medienblock haette ihn ersetzt (R16 W2 / R15 W1).
 * `transform` steht bei ihnen in der EIGENEN `transition` (aus
 * `PRESS_TRANSITION`), hier bleibt nur der Druck.
 */
export const TOUCH_TARGET_COARSE_ACTIVE = {
  '@media (pointer: coarse)': {
    ...TOUCH_TARGET_COARSE['@media (pointer: coarse)'],
    '&:active': { transform: `scale(${PRESS_SCALE})` },
  },
};

/** Dasselbe fuer runde Knoepfe — beide Achsen (`TOUCH_TARGET_COARSE_QUADRAT`).
 *  Ohne `minWidth` wuerde ein `MuiIconButton` (MUIs `borderRadius: '50%'`) mit
 *  `minHeight` allein **28 × 44**, ein Oval. */
export const TOUCH_TARGET_COARSE_QUADRAT_ACTIVE = {
  '@media (pointer: coarse)': {
    ...TOUCH_TARGET_COARSE_QUADRAT['@media (pointer: coarse)'],
    '&:active': { transform: `scale(${PRESS_SCALE})` },
  },
};

/** Control height — 32 px Desktop, 44 px mobil UND bei grobem Zeiger.
 *  Hausform `{ xs: 44, sm: 32 }` (≥10 Stellen, s. `MovePillStrip.jsx:224`).
 *  🔴 Die Zeiger-Abfrage gehört dazu (`pattern-library.md:47`): „a touch laptop
 *  is wide and still has no hover". `min-height` schlägt `height`, die 44 px
 *  halten also auch bei ≥600 px auf grobem Zeiger.
 *  → `sx={{ ...CONTROL_HEIGHT_SX, flexShrink: 0 }}` */
export const CONTROL_HEIGHT_SX = {
  height: { xs: 44, sm: 32 },
  minHeight: { xs: 44, sm: 32 },
  ...TOUCH_TARGET_COARSE,
};

/** FELDhoehe — die Hoehe eines `size="small"`-Feldes. **Knopf UND Feld tragen sie.**
 *
 *  Hausform: `{ xs: 44, sm: 40 }` plus die Zeigerabfrage — dieselbe Form wie
 *  `CONTROL_HEIGHT_SX`, andere Zahl. Wo ein `small`-Feld und ein Knopf in
 *  EINER Zeile stehen („Manual Update: [Feld] [Save Price]"), tragen BEIDE
 *  diesen Wert: der Knopf per `sx={{ ...FIELD_HEIGHT_SX }}`, das Feld per
 *  `sx={{ '& .MuiInputBase-root': { ...FIELD_HEIGHT_SX, alignItems: 'center' } }}`.
 *
 *  🔴 **Bis zum 2026-10-08 stand hier „der Knopf traegt diesen Wert und das Feld
 *  bleibt, wie es ist" — das war gemessen falsch.** Die Annahme war, ein
 *  `size="small"`-Feld sei auf dem Desktop von selbst 40 px. Es ist **37,125 px**:
 *  MUI rechnet die `small`-Hoehe aus `1.4375em` Zeilenhoehe plus zweimal 8,5 px
 *  Innenabstand, und `em` ist hier die Eingabeschrift. Das Haus-Theme setzt
 *  `htmlFontSize: 14` (`theme/theme.js:310`), `1rem` ist also 14 px —
 *  `1,4375 × 14 + 17 = 37,125`. MUI's eigene 40 px gelten bei 16 px Schrift; die
 *  „40 px" im Kommentar an `theme/theme.js:878-885` wurden auf einem
 *  **390-px-Schirm** gemessen, wo dieselbe Datei die Eingabeschrift auf 16 px
 *  zwingt (`MuiInputBase.input`, `theme/theme.js:763-767`) — richtig dort, falsch
 *  auf dem Desktop. Gemessen am 2026-10-08 von `tests/design/ui2-feldzeilen.spec.ts`
 *  Z.3 (Playwright, 1440 px, Manual-Update-Zeile auf `/products/<sku>`):
 *  **Feld 37,1 px gegen „Save Price" 40,0 px.** Der Token trat damit gegen eine
 *  Ungleichheit an, die er nur halb behob — er hob den Knopf auf die ANGENOMMENE
 *  Feldhoehe, nicht auf die echte.
 *
 *  🔴 Warum nicht `CONTROL_HEIGHT_SX` (32)? Ein Knopf auf 32 px neben einem
 *  37-px-Feld ist genau die Ungleichheit, gegen die dieser Token antritt —
 *  Regel-14-Grill B1 hat die erste Fassung dieser Zeile („Knopf bekommt
 *  `CONTROL_HEIGHT_SX`") deshalb verworfen. `CONTROL_HEIGHT_SX` bleibt richtig
 *  fuer die KNOPFZEILE: dort steht kein Feld
 *  (`rma/components/EinordnungCard.jsx:432`), und ein Kopf ist eine Knopfzeile,
 *  keine Formularzeile.
 *
 *  🔴 `alignItems: 'center'` gehoert zum Feld-Aufruf: die Feldwurzel ist ein
 *  Flex-Kasten, und ohne die Zeile saesse der Text in der um 2,875 px
 *  gewachsenen Wurzel oben statt mittig.
 *
 *  🔴 Was dieser Token NICHT behebt, damit es nicht als geloest gilt: MUI's
 *  Etikett-Transformationen (`translate(14px, 9px)` / `-9px`) sind auf eine
 *  40-px-Wurzel gerechnet — mit dem Token stimmen sie wieder. Ein Feld-Boden
 *  fuer Felder OHNE Knopf daneben bleibt offen und waere eine Aenderung an
 *  `theme.js`. Auf grobem Zeiger tragen jetzt BEIDE 44 px, weil beide dasselbe
 *  Token tragen.
 *
 *  → `sx={{ ...FIELD_HEIGHT_SX }}` am Knopf **und**
 *    `sx={{ '& .MuiInputBase-root': { ...FIELD_HEIGHT_SX, alignItems: 'center' } }}`
 *    am Feld derselben Zeile.
 *
 *  *Was diesen Token widerlegt:* eine Browsermessung, in der Feld und Knopf
 *  derselben Zeile mehr als 1 px auseinanderliegen. Die 37,1 px vom 2026-10-08
 *  haben genau das getan und die Zahl damit aus der Messung geholt. */
export const FIELD_HEIGHT_SX = {
  height: { xs: 44, sm: 40 },
  minHeight: { xs: 44, sm: 40 },
  ...TOUCH_TARGET_COARSE,
};

/** MINDESTBREITE eines `Autocomplete`-Poppers auf schmalen Ankern.
 *
 *  Anlass (UI2 Teil 10, G15e NB3 / R16-W3): MUI setzt die Popperbreite auf
 *  `anchorEl.clientWidth`. In einer engen Zeile (Medien-Dialog, 390 px: Feld
 *  ~110–175 px neben einem 160-px-`select`) brach der Leerzustand — Echo plus
 *  „Clear search" — in vier Zeilen und klappte nach oben ueber das Nachbarfeld.
 *  Die laengere neue Kopie hat die vorbestehende Degradierung sichtbar gemacht.
 *
 *  🔴 `min()` und keine feste Breite: auf einem 390-px-Schirm sind 88 vw
 *  ≈ 343 px, die 20 rem (bei `htmlFontSize: 14` = 280 px) deckeln darunter —
 *  der Popper bleibt im Viewport. Ab `sm` ist das Feld ≥ 300 px
 *  (`ProductSearchSelect` setzt `minWidth: { xs: 0, sm: 300 }`), also **aendert
 *  sich am Desktop nichts** (280 < 300).
 *
 *  → `slotProps={{ popper: { sx: AUTOCOMPLETE_POPPER_MIN_WIDTH_SX } }}` */
export const AUTOCOMPLETE_POPPER_MIN_WIDTH_SX = {
  minWidth: 'min(88vw, 20rem)',
};

/** Der MIKRO-INLINE-KNOPF — ein Textknopf INNERHALB einer Datenzeile.
 *
 *  Anlass (E9-Nachzug, 2026-10-07): dieselbe fuenfzeilige `sx` stand SIEBEN
 *  Mal in EINER Datei (`PODetail.jsx`) — an der Kredit-Zeile, am AVIS-Block
 *  (Unlink / Cancel AVIS / Re-check), am Sendungs-Block (Bearbeiten / Loeschen)
 *  und am Dialog-Ausloeser. Sieben Kopien eines Wertes sind das, wofuer ein
 *  Token da ist; die achte Stelle haette die achte Kopie bekommen.
 *
 *  🔴 DAS IST KEIN GEWOEHNLICHER `Button`, UND DESHALB STEHT ER HIER UND NICHT
 *  ALS THEME-OVERRIDE. Er soll die KNOPFHOEHE EINER ZEILE NICHT tragen:
 *  `minHeight: 0` nimmt den Theme-Boden zurueck, weil der Knopf in einer
 *  INFOZEILE sitzt und nicht in einer Knopfzeile — eine Zeile, die auf 44 px
 *  waechst, schoebe genau das Layout auseinander, das sie beschreibt (§4.1a,
 *  „dichte Kontexte"). Die drei Knopf-Wurzeln im Theme bleiben unberuehrt.
 *
 *  🔴 **WIE WEIT diese Ruecknahme reicht — gemessen 2026-10-10** (Orchestrator,
 *  `/rma/1888`, Knopf „Fetch from Shopify", `getComputedStyle(knopf).minHeight`):
 *
 *  | Breite  | Zeiger | minHeight | Hoehe |
 *  |---------|--------|-----------|-------|
 *  | 390 px  | grob   | 44 px     | 44    |
 *  | 390 px  | fein   | 44 px     | 44    |
 *  | 1440 px | grob   | 44 px     | 44    |
 *  | 1440 px | fein   | 0 px      | 21    |
 *
 *  *Kalibrierung:* ein `Button size="small"` OHNE diese Konstante misst 44 px
 *  unter `sm` und auf grobem Zeiger, 28 px bei 1440 px mit feinem Zeiger — die
 *  Messung sieht den Theme-Boden also.
 *
 *  🔴 **Die Ruecknahme greift damit NUR ab `sm` mit FEINEM Zeiger.** Unter `sm`
 *  und auf jedem groben Zeiger gewinnen die zwei Theme-Boeden aus `theme.js`
 *  (`MuiButton.styleOverrides.root`: `down('sm')` 44 px und der Zeigerboden aus
 *  `TOUCH_TARGET_COARSE_ACTIVE`, ebenfalls 44 px) — der Mikro-Knopf ist dort
 *  44 px hoch, nicht 21.
 *
 *  ⚠️ Hier stand bis 2026-10-10 das Gegenteil — „der Knopf liegt damit AUCH auf
 *  einem groben Zeiger unter 44 px. Das ist der bewusste Preis fuer die dichte
 *  Zeile." Der Satz war eine Analogie zu einem anderen `sx`-Fall und ist durch
 *  die Messung widerlegt: **diesen Preis gibt es nicht.** Der Nutzen des Tokens
 *  liegt auf dem Desktop; auf dem Telefon ist die Zeile NICHT dichter als eine
 *  gewoehnliche Knopfzeile. Wer sie dort dichter braucht, muss die Spezifitaet
 *  heben — **nicht gebaut** (Karte K-4, Entscheid E-4).
 *
 *  ⚠️ Die 11 sind der GEMESSENE Wert dieser Stellen, NICHT `FS.xs` (11,5).
 *  Wer die Schriftgroesse vereinheitlichen will, aendert hier UND sieht die
 *  Aenderung dann an allen sieben Stellen — das ist der Zweck.
 *
 *  Verbraucher setzen `color`/`alignSelf` daneben; die zwei Ausnahmen mit mehr
 *  Polster (`py: 0.25, px: 1`) ueberschreiben die zwei Felder ausdruecklich.
 *
 *  → `sx={{ ...MICRO_INLINE_BUTTON_SX, color: 'text.secondary' }}` */
export const MICRO_INLINE_BUTTON_SX = {
  fontSize: 11,
  minHeight: 0,
  py: 0,
  px: 0.75,
};

/**
 * Wie viel Platz die schwebende Unterleiste (`MobileBottomNav`) am unteren Rand
 * belegt — plus der Abstand, den ein SCHWEBENDES Element darueber halten muss.
 *
 * 🔴 Die Zahl ist NICHT geraten; sie kommt aus der Leiste selbst
 *    (`components/MobileBottomNav.jsx`):
 *
 *      Pillen-Zeile           56 px   `PillNavButton` 48 px + `py: 0.5` (4 px je Seite)
 *      unteres Polster        14 px   `pb: 'calc(env(safe-area-inset-bottom) + 14px)'`
 *      ─────────────────────────────
 *      belegt                 70 px   (+ `env(safe-area-inset-bottom)`)
 *      Luft bis 96            26 px
 *
 *    Die runden Knoepfe daneben sind 52 px hoch und damit NIEDRIGER als die
 *    Pille; die 56 px sind also die tragende Zeilenhoehe.
 *
 * 🔴 Die Herleitung beschreibt die TREFFERFLAECHE, nicht die sichtbare Zone —
 *    der Unterschied ist gemessen, nicht angenommen:
 *
 *      Trefferflaeche         70 px   Pille + Polster; darunter faengt die Leiste nichts ab
 *      sichtbare Zone        118 px   + `pt: 6` (48 px) Verlauf ueber der Pille
 *
 *    Der Verlauf ist da, hat aber keine Trefferflaeche (`pointerEvents: 'none'`
 *    am Rahmen, `MobileBottomNav.jsx:280`) und ist an seiner Oberkante
 *    vollstaendig durchsichtig. 🔴 **M1 (2026-10-09): er ist am unteren Anschlag
 *    jetzt DECKEND** (aus der Palette, kein `rgba`-Literal) statt 0,28 bzw. 0,22
 *    durchsichtig — der durchscheinende Verlauf liess den Inhalt unter der Pille
 *    stehen. Ein Fuss auf `bottom: 96px` sitzt deshalb 22 px IN dieser
 *    Verlaufsflaeche; auf dieser Hoehe traegt der Verlauf jetzt rund **0,19**
 *    Deckkraft (vorher unter 0,06). Er liegt mit `zIndex: 1200` (`:272`) ueber
 *    dem Fuss (`bgcolor: 'background.paper'`, `TableFooterSimple.jsx:58`) —
 *    dessen unterste 22 px faden also in die Leiste. Fuer die Konstante zaehlt
 *    weiterhin die Trefferflaeche, nicht die sichtbare Zone.
 *
 * 🔴 Sie stand bis zum 2026-10-07 ZWEIMAL als Literal `96` im Repo
 *    (`theme.js` Snackbar, `App.js` Toaster) und FEHLTE an der dritten Stelle,
 *    an der sie gebraucht wird: den schwebenden Tabellenfuessen. Die sassen auf
 *    `bottom: 0` bzw. `bottom: 25` und verschwanden mobil HINTER der Leiste —
 *    „Products: 235 · 1–50 of 235" war nicht mehr lesbar.
 *
 * 🔴 Gilt nur unterhalb `sm` (< 600 px): dort rendert
 *    `SidebarAndHeaderBar.jsx` den Mobil-Zweig samt Leiste
 *    (`useMediaQuery(theme.breakpoints.down('sm'))`).
 */
export const MOBILE_BOTTOM_NAV_CLEARANCE_PX = 96;

/** Dieselbe Zahl als CSS-Ausdruck, inkl. Geraeterand. Fuer `bottom:` in `sx`. */
export const MOBILE_BOTTOM_NAV_CLEARANCE =
  `calc(env(safe-area-inset-bottom, 0px) + ${MOBILE_BOTTOM_NAV_CLEARANCE_PX}px)`;

/** 🔴 G0.9 (UI2 Teil 10, 2026-10-12, R16 C1): die Deckung als `sx`-Block.
 *
 *  Eine mobile Flaeche, die die Haus-Unterleiste **abdecken** soll, braucht
 *  `minHeight: MOBILE_BOTTOM_NAV_CLEARANCE` (die **`calc`-Form**, nicht `…_PX`)
 *  — und zwar **im Medienblock**.
 *
 *  🔴 **Warum die `calc`-Form und nicht die blosse Zahl (R16 Bedingung 2,
 *  2026-10-12).** Der Kasten ist `box-sizing: border-box` (`App.js` rendert
 *  `CssBaseline`) und traegt den Geraeterand als `pb` **in sich**. Mit der
 *  PX-Form ist seine Grenzbox deshalb `96 px` hoch, egal wie gross der Rand ist;
 *  die Unterleiste dagegen **addiert** ihn (`MobileBottomNav.jsx`:
 *  `pb: 'calc(env(safe-area-inset-bottom) + 14px)'`). Ihre Bandoberkante liegt
 *  damit auf `env(safe-area-inset-bottom) + 70 px` (14 px Polster + 56 px Band:
 *  4 + 48 + 4, die mittlere Glaspille ist das hoechste Kind).
 *  Mit der PX-Form deckte die Leiste also nur bis `Rand ≤ 26 px` — auf einem
 *  Geraet mit Home-Indikator blieb ein Streifen des Bandes stehen. Mit der
 *  `calc`-Form ist die Grenzbox `96 px + Rand` hoch, und der **Unterschied**
 *  gegen die Bandoberkante ist randunabhaengig `96 − 70 = 26 px`: sie deckt bei
 *  **jedem** Geraeterand. ⚠️ Genau diese Randunabhaengigkeit hatte die
 *  Runde-2-Fassung der PX-Form zugeschrieben — sie gilt fuer zwei Kaesten, die
 *  **beide** auf dem Rand sitzen, und der `min-height`-Kasten ist keiner davon.
 *
 *  🔴 **Warum der Medienblock mitmuss.** `FIELD_HEIGHT_SX` traegt
 *  `...TOUCH_TARGET_COARSE` (`{ '@media (pointer: coarse)': { minHeight: 44 } }`),
 *  und emotion emittiert diesen Block **nach** der Grundregel. Eine blanke
 *  `minHeight`-Zusage daneben verliert deshalb auf genau den Geraeten, fuer die
 *  sie gedacht ist: gemessen an `BulkActionBar` stand am Ende
 *  `min-height: 96px` **und** `@media (pointer: coarse) { min-height: 44px }`
 *  daneben — der grobe Zeiger bekam 44. Deshalb traegt **auch der Medienblock
 *  die `calc`-Form**; stuende dort `MOBILE_BOTTOM_NAV_CLEARANCE_PX`, schluege er
 *  die `calc`-Form der Grundregel auf Touch-Geraeten wieder aus. Dieser Token
 *  traegt den Block mit, dieselbe Bauform wie `TOUCH_TARGET_COARSE_ACTIVE`.
 *
 *  `alignItems: 'center'` steht mit dabei: bei dieser Mindesthoehe soll die Zeile
 *  in der **Mitte** des Bandes stehen, nicht an seinem oberen Rand. */
export const MOBILE_BOTTOM_NAV_CLEARANCE_SX = {
  minHeight: MOBILE_BOTTOM_NAV_CLEARANCE,
  alignItems: 'center',
  '@media (pointer: coarse)': { minHeight: MOBILE_BOTTOM_NAV_CLEARANCE },
};

/** 🔴 G0.9 (UI2 Teil 10, 2026-10-12): die STAPELEBENE der mobilen Unterleiste.
 *
 *  🔴 **Das ist eine ABSCHRIFT, keine Quelle** (R15 W-2, 2026-10-12). Der Wert
 *  steht im Baum als **Literal** an `MobileBottomNav.jsx:272` (`zIndex: 1200,`);
 *  diese Konstante fuehrt ihn nur benannt, damit ein Baustein, der sich darueber
 *  legen MUSS, ihn nicht ein zweites Mal tippt. Bis zum 2026-10-12 stand hier
 *  „Regel 22: ein Wert, eine Quelle" — das war die Behauptung einer Herkunft,
 *  die es nicht gibt: die Unterleiste liest diese Konstante **nicht**.
 *
 *  🔴 **Was die Abschrift zusammenhaelt, ist eine Probe, kein Kommentar:**
 *  `BulkActionBar.test.jsx` liest die Zahl aus dem **Quelltext** von
 *  `MobileBottomNav.jsx` (`/zIndex:\s*(\d+)/`, genau ein Treffer) und verlangt,
 *  dass die gerenderte Leiste darueber liegt — mit `1100` als Kalibrierung.
 *  Wer die Unterleiste umstellt, wird also rot, auch wenn diese Zeile stehen
 *  bleibt.
 *
 *  🔴 **Die eine Quelle herzustellen ist NICHT dieser Schnitt.** Sie hiesse, dass
 *  die Unterleiste den Token importiert — das ist verhaltensneutral, aber **nicht
 *  additiv** (`MobileBottomNav.jsx` bleibt hier zeichengleich, s. `git diff
 *  origin/master`), und sie liegt deshalb bei **G1**. Wer sie baut, zieht diese
 *  Zeile mit.
 */
export const MOBILE_BOTTOM_NAV_Z_INDEX = 1200;

/** 🔴 G0.9 (UI2 Teil 10, 2026-10-12): die mobile Sammelleiste steht AN DER
 *  STELLE der Unterleiste.
 *
 *  §11.6 woertlich: „Aktionsleiste unten (im Modus, an der Stelle der
 *  Bottom-Navigation)". „An der Stelle" heisst: sie **ersetzt** sie im Modus.
 *  Ohne den hoeheren Wert saesse die Leiste HINTER der Unterleiste: beide tragen
 *  `position: fixed`, `bottom: 0`, `left: 0`, `right: 0`, und der Verlauf der
 *  Unterleiste ist an seinem unteren Anschlag DECKEND — die Leiste waere
 *  unsichtbar, aber klickbar (`pointerEvents: 'none'` gilt nur fuer den
 *  Verlaufsrahmen).
 *
 *  🔴 **Diese Konstante allein DECKT NICHTS AB — sie stapelt nur.** R16 C1
 *  (2026-10-12) hat genau diese Verwechslung gefunden: ein Wert ueber 1200 legt
 *  die Leiste *vor* die Unterleiste, aber ein 44 px hoher Balken vor einem 66 px
 *  hohen Knopfband laesst dessen obere 22 px hervorschauen. Die **Hoehe** ist die
 *  zweite Haelfte und steht in `MOBILE_BOTTOM_NAV_CLEARANCE_SX`; beide zusammen
 *  tragen die Zusage. Wer nur diese Zeile liest, liest den halben Vertrag.
 *
 *  Die Zahl kommt aus der Hausleiter: `MOBILE_BOTTOM_NAV_Z_INDEX` (1200) plus
 *  zehn — kein zweiter unabhaengiger Wert. */
export const MOBILE_SELECTION_BAR_Z_INDEX = MOBILE_BOTTOM_NAV_Z_INDEX + 10;

/** 🔴 N4E PR C, Nachbesserung 2 (2026-10-16): die STAPELEBENE eines OFFENEN
 *  Leisten-Stapels (`ContextDrawer`, zweite Ebene).
 *
 *  🔴 **Der Anlass ist gemessen, nicht angenommen.** Mit offenem Blatt bei 390
 *  liegt die Ebene 2 im Dokument (y 434 bis 890), und der angedockte Verfasser
 *  (`Verfasser.jsx:840`, `position: fixed`, `zIndex: 5`) deckt davon das Band
 *  810 bis 844. Ein 9x9-Raster ueber das Blatt traf dort neun Punkte auf
 *  `BUTTON[verfasser-modus-ask]` und acht auf `DIV[fall-verfasser]`. Weil der
 *  innere Bildlauf des Blatts genau so hoch ist wie das Blatt
 *  (`context-drawer-untermenue-inhalt`, `overflowY: auto`), war der letzte
 *  Streifen seines Inhalts damit unerreichbar.
 *
 *  🔴 **Warum die Zahl aus der Hausleiter kommt und nicht `6` heisst:** ein Wert
 *  knapp ueber `5` waere eine zweite, unabhaengige Zahl, die beim naechsten
 *  Umbau der unteren Leisten still falsch wird. Die Hausleiter hat EINEN oberen
 *  Wert fuer Seitenchrome; die Fallansicht zeichnet am Telefon keine
 *  Unterleiste, es gibt also nichts, was darunter liegen muesste.
 *
 *  Gesetzt wird sie NUR, solange ein Stapel offen ist (`stapelOffen`) — also
 *  genau dann, wenn die Leiste die Seite ueberlagern soll. Ohne offenes Blatt
 *  bleibt die Leiste im Fluss und der Verfasser darueber. */
export const OFFENER_STAPEL_Z_INDEX = MOBILE_BOTTOM_NAV_Z_INDEX;

/** 🔴 M1 (2026-10-09): der Unterrand der mobilen AppBar —
 *  `borderBottom: '1px solid rgba(255,255,255,0.08)'` an
 *  `SidebarAndHeaderBar.jsx:2452`. Er gehoert in den Kopfabstand: der Kasten der
 *  Leiste ist 44 + 1 = 45 px hoch, und `main` muss auf 45 beginnen, nicht auf
 *  44. Ohne ihn liegt der Inhalt 1 px unter der Leiste. */
export const MOBILE_APP_BAR_BORDER_PX = 1;

/** 🔴 M1 (UI-Durchgang 2, Teil 5, 2026-10-09): der Kopfabstand der Schale —
 *  Leistenshoehe PLUS Geraeterand. EINE Quelle fuer die vier Stellen, die ihn
 *  bis dahin als feste `44px` trugen (`SidebarAndHeaderBar.jsx`: `top` des
 *  Such-Backdrops, `maxHeight` des Such-Panels, `mt` und `minHeight` der
 *  Inhaltsflaeche).
 *
 *  Die 44 ist die HOEHE DER LEISTE selbst (`SidebarAndHeaderBar.jsx:2467`,
 *  `minHeight: 44` am `Toolbar`) und **kein Tippziel** — MUI's Vorgabe waere
 *  56 px; die Begruendung steht dort (E9, 2026-10-07).
 *
 *  🔴 **Dazu der Unterrand der AppBar (`MOBILE_APP_BAR_BORDER_PX`).** Die Leiste
 *  misst nicht 44, sondern **45** px: `getBoundingClientRect()` schliesst den
 *  `borderBottom` ein. Mit `mt: 44px` begann die Inhaltsflaeche 1 px UNTER der
 *  Leiste — das war schon vor M1 so (gemessen auf `master`: Leiste 45,
 *  Inhaltsflaeche 44) und ist hier mitbehoben.
 *
 *  🔴 Seit `viewport-fit=cover` (`frontend/public/index.html:9`) loesen die
 *  `env(safe-area-inset-*)` NICHT mehr zu 0 auf. Auf einem Geraet mit
 *  Home-Indikator traegt die AppBar `pt: 'env(safe-area-inset-top, 0px)'` und
 *  wird damit um das Polster HOEHER; ohne diese Konstante rutschte die
 *  Inhaltsflaeche unter die Statusleiste.
 *
 *  ⚠️ Benutzt an den vier ABGELEITETEN Stellen, NICHT am `Toolbar`: dort traegt
 *  die AppBar das Polster schon als `pt`, ein zweiter Einsatz zaehlte es
 *  doppelt.
 *
 *  Gilt nur unterhalb `sm` (< 600 px) — dort rendert `SidebarAndHeaderBar.jsx`
 *  den Mobil-Zweig. */
export const MOBILE_APP_BAR_CLEARANCE =
  `calc(env(safe-area-inset-top, 0px) + 44px + ${MOBILE_APP_BAR_BORDER_PX}px)`;

/** 🔴 M2 (2026-10-16) — das Polster der Suchleiste im Mobil-Panel, EIN Wert
 *  fuer BEIDE Kanten.
 *
 *  Anlass: `pt: 1.25` (10 px) gegen `pb: calc(env(safe-area-inset-bottom) +
 *  12px)` — bei offener Tastatur sind die zwei Kanten **ungleich**, und der
 *  Owner hat genau das im Foto `owner_5_glas_abstand.png` beanstandet
 *  („die Abstaende … komisch gross und ungleich im Vergleich mit oben").
 *  Der **groessere** der zwei bestehenden Werte gewinnt: die Beschwerde lautet
 *  „zu eng" und nicht „zu weit", und 12 px ist der Wert, den die `calc`-Form
 *  unten schon traegt.
 *
 *  🔴 **MUI-Einheit, nicht px.** In `sx` sind `1.5` = `12px` (Theme-Spacing 8),
 *  und dieselbe Zahl traegt `pt` UND `pb`. Die Ableitung der Mindesthoehe
 *  rechnet sie mit `* 8` in px um — eine zweite px-Zahl daneben waere die
 *  zweite Wahrheit.
 *
 *  *Was diesen Token widerlegt:* eine Geraetemessung, in der `pt` und `pb` bei
 *  offener Tastatur auseinanderlaufen, oder eine, in der 12 px oben zu eng
 *  sind. */
/** 🔴 Suche mobil v2, Stufe 1 (2026-10-31) — **der Rand INNEN an der Feldzeile**
 *  (MUI-Einheit, 8 px): oberhalb des Feldes, zwischen Feld und Chip-Zeile und
 *  unterhalb der Chip-Zeile.
 *
 *  Er ersetzt `SUCHLEISTE_POLSTER` (1,5 = 12 px), das den Abstand um die
 *  schwebende Kapsel beschrieb. Die Kapsel gibt es nicht mehr (Plan §9.1); das
 *  Feld sitzt am oberen Rand des Panels, und die 8 px sind Polster INNEN —
 *  nicht ein Zuschlag vor der Panel-Oberkante. Genau diese Verwechslung hat die
 *  M3-Fassung 2 den sicheren Bereich doppelt zaehlen lassen (Plan §2.3, W5). */
export const SUCHE_V2_RAND = 2;
// 🔴 Stufe 6a (2026-11-04): 1 (= 8 px) → **2 (= 16 px)**. Der Seitenrand der
// Feldzeile, der Chip-Zeile und der Recents. Die Luecke ZWISCHEN Feld und Chips
// bleibt sichtbar 16, wird aber 8/8 geteilt (`SUCHE_LUECKE_HALB`, §2.3/B3) —
// die Tippflaeche der Chips ragt 8 px ueber die Kapsel und muss in der
// Polsterbox liegen.

/** 🔴 Die Hoehe der **Feldzeile** (48 px) — bis v2 `SUCHLEISTE_BEDIENHOEHE_PX`.
 *
 *  Stufe 6a: 44 → **48** (Akz. 1). Vier weitere Leser ziehen mit
 *  (Recents-Kopf, „Clear recent searches" `minWidth`/`minHeight`, Recents-Zeile,
 *  Schliessen-Kreis) — das ist Absicht, kein Nebenwirkungstreffer (§2.1/W3).
 *  Der Schliessen-Knopf ist mit **48** genauso gross wie die Zeile; er ist
 *  nicht mehr das kleinere Mass (bis Stufe 6a: 34 neben 44, Grill-R2 W6b). */
export const SUCHE_FELD_HOEHE_PX = 48;
// 🔴 Stufe 6a: 44 → **48**. Vier weitere Leser ziehen mit (Recents-Kopf,
// „Clear recent searches" `minWidth`/`minHeight`, Recents-Zeile, Schliessen-
// Kreis) — das ist Absicht, kein Nebenwirkungstreffer (§2.1/W3).

/** 🔴 Der Radius der Feldzeile — ersetzt `SUCHE_KAPSEL_RADIUS` (`'99px'`).
 *
 *  **Mit Einheit** (`'999px'`): eine nackte Zahl in `sx` ist ein Vielfaches von
 *  `theme.shape.borderRadius` (6) und ergaebe 5994 px (dieselbe Falle wie bei
 *  `SUCHE_KARTE_RADIUS`). 999 px ist die Hausform fuer „Kapsel" — der Radius
 *  ist damit die halbe Hoehe (48/2 = 24), genau die Vorlage (`Pill.jsx:215`). */
export const SUCHE_FELD_RADIUS = '999px';
// 🔴 Stufe 6a: `'10px'` → **volle Pille**. Radius = halbe Hoehe (48/2); eine
// 999-px-Kurzform ist die Hausform fuer „Kapsel" (`Pill.jsx:215`).

/** Die Hoehe der **Chip-Zeile** (Tippziel, 44 px). Eingefuehrt mit Stufe 1 als
 *  Baustein der Panel-Untergrenze; der Verbraucher kommt mit den Chips
 *  (Stufe 2). */
export const SUCHE_CHIP_HOEHE_PX = 28;
// 🔴 Stufe 6a: 44 → **28**, und zwar als **feste `height`** an der Kapsel, nicht
// als `minHeight`: `minHeight` ist ein Boden UNTER dem Inhalt, und bei
// `FS.lg`/1,4 stuende der Inhalt auf 29 px (§2.3/B2). Die 44-px-Tippflaeche
// traegt der Chip ueber ein `::after` (§2.3/B3).

/** Die Kachel links in der mobilen Trefferzeile (Plan §2.2/§4.2, Stufe 2).
 *
 *  Traegt das Art-Icon (Stufen 2–3) bzw. spaeter das Produktbild (Stufe 4).
 *  Sie ist **40** und damit kleiner als die zweizeilige Inhaltshoehe (42 +
 *  19,5): die Zeile wird von Titel und Metazeile bestimmt, nicht von der
 *  Kachel — `16 + max(40, Inhalt)` = **77,5** px bei zwei Titelzeilen (§2.2). */
export const SUCHE_ZEILE_BILD_PX = 48;
// 🔴 Stufe 6a: 40 → **48**. Die Kachel bestimmt jetzt das Zeilenraster mit
// (24 Polster + 48 = **72** einzeilig, §2.2/§2.4).

/** Die Groesse der kleinen Symbole IN der Suche — Lupe im Feld, ✕, Uhr der
 *  „Recent"-Zeile (Stufe 3).
 *
 *  Sie steht hier, weil dieselbe Zahl an mehreren Stellen gebraucht wird und
 *  ein roher `fontSize` in `sx` gegen Regel 22 laeuft. **17** ist der Wert, den
 *  Lupe und ✕ im Feld seit Stufe 1 tragen; die Uhr der Recents nimmt ihn auf,
 *  damit die Zeile nicht groesser wirkt als das Feld darueber.
 *  🔴 Seit der Nachbesserung 1 (2026-11-01, R15-W2) lesen **alle drei** den
 *  Token — bis dahin trugen Lupe und ✕ den Wert roh, und dieser Kommentar
 *  behauptete das Gegenteil. */
export const SUCHE_IKON_PX = 17;

/** Die Zahl der Titelzeilen der mobilen Trefferzeile (Plan §4.2, F2-A).
 *
 *  Der Titel kuerzt per `-webkit-line-clamp`; zwei Zeilen sind 2 × 21 = 42 px
 *  und gehen als einziger Posten in die 77,5-px-Zeile ein, die A1 = 9 traegt.
 *  Eine Mutation auf 1 macht die Zeile 56,5 px hoch — die Abnahme (§10 Stufe 2)
 *  misst das an der Zeile, nicht am Token. */
export const SUCHE_TITEL_ZEILEN = 2;

// ── Suche mobil v2, Stufe 6a (2026-11-04): Zeile, Chip-Schale, Navi-Reserve ──
// Die 16 neuen Tokens des Schnitts (§2.1). Jedes Pixel-Token traegt `…_PX` im
// Namen; Spacing-Tokens sind MUI-Einheiten (`theme.spacing(n)` = 8n px).
// 🔴 KEIN `SUCHE_ZEILE_RASTER_PX` (R15-W4, 2026-11-04): das Zeilenraster
// **72** einzeilig (24 Polster + 48 Kachel), **80,5** zweizeilig und **82,5**
// mit Status-Pille entsteht aus `SUCHE_ZEILE_POLSTER` und
// `SUCHE_ZEILE_BILD_PX` und wird von E6 gemessen. Ein Token ohne Leser waere
// ein zweiter Name fuer dieselbe Zahl — genau der Grund, aus dem der Plan
// `SUCHE_VERLAUF_HOEHE` gestrichen hat (Grill R2 W-F).

/** Das senkrechte Polster der Trefferzeile — MUI-Einheit (1.5 = 12 px je
 *  Seite, 24 gesamt). Loest `py: 1` (8 px) ab. */
export const SUCHE_ZEILE_POLSTER = 1.5;

/** Radius der 48er-Kachel — **mit Einheit** (`'12px'`), aus demselben Grund
 *  wie `SUCHE_FELD_RADIUS`: eine nackte Zahl in `sx` ist ein Vielfaches von
 *  `theme.shape.borderRadius`. */
export const SUCHE_ZEILE_BILD_RADIUS = '12px';

/** Schriftgroesse des Zeilentitels in px — `FS.lg` (15). „PT" heisst Bildpunkt
 *  und ist ABSICHTLICH nicht die MUI-Einheit `pt` (die waere 8 × 15 = 120). */
export const SUCHE_TITEL_HOEHE_PT = FS.lg;

/** Zeilenhoehe des Titels: 15 × 1,3 = **19,5** je Zeile (zwei Zeilen = 39). */
export const SUCHE_TITEL_ZEILENHOEHE = 1.3;

/** Zeilenhoehe des Chip-Labels: 15 × 1,2 = **18** → Kapsel 18 + 8 (`py`) + 2
 *  (Rand) = **28,0** — genau der Token `SUCHE_CHIP_HOEHE_PX` (§2.3/B2). */
export const SUCHE_CHIP_ZEILENHOEHE = 1.2;

/** Die 16-px-Luecke zwischen Feld und Chip-Zeile, **8/8 geteilt** (MUI-Einheit,
 *  1 = 8 px). Sichtbar bleibt EINE Luecke von 16; der Grund fuer die Teilung
 *  ist die Tippflaeche der Chips (§2.3/B3): die Chip-Zeile traegt
 *  `overflowY: 'hidden'` und schnitte einen Ueberstand ueber ihrer Polsterkante
 *  ab. 🔴 EINHEIT: MUI, NICHT Pixel — `pb: 8` waeren 64 px. */
export const SUCHE_LUECKE_HALB = SUCHE_V2_RAND / 2;

/** 🔴 Der senkrechte Ueberstand der Chip-Tippflaeche ueber die sichtbare
 *  Kapsel (px) — Stufe 6a.
 *
 *  Die Kapsel ist 28 hoch (`SUCHE_CHIP_HOEHE_PX`), das Tippziel 44: je Seite
 *  **8**. Der Ueberstand liegt im `&::after` des Chips, und zwar als
 *  `inset: -(UEBERSTAND + 1)px 0` — die 1 ist der 1-px-Rand von `Pill`
 *  (`Pill.jsx:231`), gegen dessen **Polsterbox** `inset` rechnet.
 *
 *  Die Zeile traegt `overflowY: 'hidden'` und schneidet an ihrer
 *  **Polsterkante** ab; ihr `pt` ist deshalb genau dieser Ueberstand
 *  (`SUCHE_LUECKE_HALB`, 8 px). Am ruhenden Panel gemessen (2026-11-04):
 *  Kapsel-Oberkante 80, Schnittkante 72, Messpunkt `cy − 21` = **73** — also
 *  **1 CSS-px innerhalb** der Kante. Eine zusaetzliche „Luft“ ueber dem
 *  Ueberstand (Nachbesserung 2) ist am 2026-11-04 **zurueckgenommen**: sie war
 *  nicht die Ursache des roten Browserlaufs (das Panel stand still unterhalb
 *  des Fensters) und verschob nur die Rechnung. */
export const SUCHE_CHIP_UEBERSTAND_PX = 8;

/** Hoehe des Trefferzahl-Abzeichens am Chip (px, feste `height`). */
export const SUCHE_CHIP_ABZEICHEN_HOEHE_PX = 20;

/** Mindestbreite des Abzeichens (px) — gegen den Breitensprung 9 → 10 bei
 *  `tabular-nums`. */
export const SUCHE_CHIP_ABZEICHEN_MIN_BREITE_PX = 20;

/** Der Verlaufsrand der mobilen Unterleiste (px): der Unterschied gegen die
 *  Bandoberkante ist randunabhaengig `96 − 70 = 26` (`tokens.js`,
 *  `MOBILE_BOTTOM_NAV_CLEARANCE_SX`).
 *  🔴 Noch ohne Leser (R15-W4/R16-W7): der Verbraucher kommt mit **Stufe 6b**
 *  (Navi-Huelle unter dem offenen Panel). */
export const MOBILE_BOTTOM_NAV_VERLAUF_RESERVE_PX = 26;

/** Die Oberkante des Knopfbandes (px) — **abgeleitet**, nicht ein zweites Mal
 *  getippt: `MOBILE_BOTTOM_NAV_CLEARANCE_PX − MOBILE_BOTTOM_NAV_VERLAUF_RESERVE_PX`
 *  = 96 − 26 = 70.
 *  🔴 Noch ohne Leser (R15-W4/R16-W7): der Verbraucher kommt mit **Stufe 6b**. */
export const MOBILE_BOTTOM_NAV_BAND_OBERKANTE_PX =
  MOBILE_BOTTOM_NAV_CLEARANCE_PX - MOBILE_BOTTOM_NAV_VERLAUF_RESERVE_PX;

/** Die Reserve der Suche ueber der Unterleiste (px): Bandoberkante + 8 px Luft
 *  = **78**. Die `calc`-Form darunter traegt den Geraeterand.
 *  🔴 Noch ohne Leser (R15-W4/R16-W7): der Verbraucher kommt mit **Stufe 6b**. */
export const SUCHE_NAVI_FREI_PX = MOBILE_BOTTOM_NAV_BAND_OBERKANTE_PX + 8;

/** Dieselbe Reserve als CSS-Ausdruck, inkl. Geraeterand. Fuer die CSS-Variable
 *  `--suche-navi-frei`.
 *  🔴 Noch ohne Leser (R15-W4/R16-W7): der Verbraucher kommt mit **Stufe 6b**. */
export const SUCHE_NAVI_FREI =
  `calc(env(safe-area-inset-bottom, 0px) + ${SUCHE_NAVI_FREI_PX}px)`;

/** Stapelebene der Navi-Huelle bei offener Suche: **1390** — ueber dem Panel
 *  (1300), UNTER Snackbar/Tooltip (1400).
 *  🔴 Noch ohne Leser (R15-W4/R16-W7): der Verbraucher kommt mit **Stufe 6b**. */
export const SUCHE_NAVI_Z_INDEX = 1390;

/** Der Schatten der Feldzeile — derselbe Wert wie `SHADOWS.card`, kein neuer. */
export const SUCHE_FELD_SCHATTEN = SHADOWS.card;

/** 🔴 **Eingefrorener Desktop-Anker** (px): der Alt-Wert der Blattformel
 *  `SUCHE_V2_RAND * 8 + SUCHE_FELD_HOEHE_PX + SUCHE_V2_RAND * 8` = 8 + 44 + 8
 *  = **60**. Er wird NICHT aus den Suchtokens abgeleitet — sonst wuerde der
 *  Desktop mit 16 + 48 + 16 = 80 mitwandern, obwohl er unveraendert bleiben
 *  soll (Akz. 6). Gebunden ist er allein durch `SearchBereichWahl.test.jsx`
 *  (V1.8), das die 60 als Literal prueft. */
export const SUCHE_BLATT_OBEN_DESKTOP_PX = 60;

/** 🔴 **ABGELEITET** — die Untergrenze der Panelhoehe, und sie steht IN der
 *  Hoehenformel, nicht als `minHeight` daneben (Plan §2.3, B5).
 *
 *  `3 · Rand + Feld + Chip` = 24 + 44 + 44 = **112**. Sie ist der Boden der
 *  Formel `max(112, vv.top + vv.hoehe − top)`: bei `vv.top = 0`, `sicher = 59`,
 *  `vv.hoehe = 100` ergaebe die reine Rechnung `41` — kleiner als Feld 44 +
 *  Rand 8 + Chips 44 + Rand 8 = 104, und die Feldzeile waere angeschnitten.
 *  Das Panel darf hoeher als der sichtbare Bereich sein (`overflow: hidden`);
 *  Feld- und Chip-Zeile liegen dann vollstaendig im Panel, die Liste rollt im
 *  Rest.
 *
 *  ⚠️ **Die Grenze, die auch dieser Boden nicht hebt:** ist der sichtbare
 *  Bereich selbst kleiner als 104 px, liegt der untere Teil hinter der
 *  Tastatur. Unvermeidbar, benannt statt behoben (Plan §2.3).
 *
 *  *Was diesen Boden widerlegt:* eine Messung, in der die Feldzeile bei
 *  `vv.hoehe = 100` ohne ihn vollstaendig im Panel liegt. */
// 🔴 Stufe 6a (§2.1/W5): die mittlere 16 entsteht seit der 8/8-Teilung aus
// ZWEI `SUCHE_LUECKE_HALB`, nicht aus `SUCHE_V2_RAND`. Die Summe bleibt
// dieselbe (2 · 16 + 2 · 8 = 48 = 3 · 16), aber wer die Teilung spaeter
// verschiebt, bekaeme mit `3 · RAND` einen still falschen Boden. Das `· 8` ist
// die Umrechnung MUI-Einheit → px, kein Abstand.
//   2·16 + 2·8 + 48 + 28 = 124   (vor Stufe 6a: 112)
export const SUCHE_PANEL_MINDESTHOEHE_PX =
  2 * SUCHE_V2_RAND * 8 + 2 * SUCHE_LUECKE_HALB * 8 +
  SUCHE_FELD_HOEHE_PX + SUCHE_CHIP_HOEHE_PX;

/** Das untere Polster der **Trefferflaeche** im Panel (MUI-Einheit) — der
 *  Scroller traegt `pb: 1` (8 px).
 *
 *  🔴 **Es gehoert in die Untergrenze des PANELS, und das ist gemessen, nicht
 *  abgeleitet.** Ein Flex-Kind kann unter seine Polsterung nicht schrumpfen:
 *  bei `box-sizing: border-box` ist die Polsterung der Boden des Randkastens,
 *  auch wenn `min-height: auto` (hier 0, weil der Scroller `overflow: auto`
 *  traegt) rechnerisch 0 ergibt. Im Tastaturzweig ist das Panel auf seine
 *  Untergrenze gefallen, der Scroller bekam deshalb **8 px** statt der
 *  rechnerischen 0,6 — und die Suchleiste stand **8,375 px** unter der
 *  Panelunterkante und wurde von `overflow: hidden` abgeschnitten.
 *
 *  Rechenweg der Messung (A/B `suche_m2b`, `M2.11`, 430 × 430 mit erzwungener
 *  Tastatur): Panel 80 px − 1 px Oberrand = 79 px Inhalt; Scroller 8 + Leiste
 *  79,375 = 87,375; Ueberhang **8,375** — genau die Zahl aus dem Befund.
 *
 *  *Was diesen Token widerlegt:* eine Messung, in der der Scroller im
 *  Tastaturzweig unter 8 px faellt oder in der die Leiste ohne diesen Zuschlag
 *  vollstaendig im Panel bleibt. */
export const SUCHPANEL_TREFFER_POLSTER = 1;

/** Die px-Form von `SUCHPANEL_TREFFER_POLSTER` fuer die CSS-Variable
 *  `--suche-treffer-polster` (Hausform der Ableitung). Sie ist seit Suche
 *  mobil v2 der **Grundwert** des unteren Listenrands: Tastatur offen → 8 px,
 *  Tastatur zu → `max(8px, env(safe-area-inset-bottom))` (Home-Indikator). */
export const SUCHPANEL_TREFFER_POLSTER_PX = SUCHPANEL_TREFFER_POLSTER * 8;

/** Die groesste Hoehe einer OFFENEN Listbox (Autocomplete-Vorschlagsliste,
 *  Auswahlmenue) — in px, fuer `slotProps.listbox`.
 *
 *  Anlass (Karte K-12, UI2 Teil 8, 2026-10-10): im `LinkCaseDialog` oeffnete
 *  `openOnFocus` die Fall-Liste sofort, und der Popper legte sich ueber
 *  `<DialogActions>` — „Cancel" war ohne Escape nicht klickbar.
 *  🔴 GEMESSEN (Nachbesserung 1, 2026-10-10, Playwright): dieses Token gibt den
 *  Knopf NICHT frei. Der Popper haengt an der Unterkante des Feldes, und die
 *  Aktionsleiste steht unmittelbar darunter — jede Kapphoehe deckt ihn. Gebaut
 *  ist deshalb der Rueckfall: `openOnFocus` ist im `LinkCaseDialog` entfernt,
 *  der Dialog oeffnet mit geschlossener Liste. Das Token bleibt als
 *  HOEHENGRENZE (die Liste endet nicht am Fensterrand), nicht als Abhilfe.
 *
 *  @useWhen
 *    - eine offene Listbox soll eine feste Hoehe nicht ueberschreiten
 *      (Lesbarkeit, gleiche Hoehe an mehreren Stellen)
 *      → `slotProps={{ listbox: { sx: { maxHeight: LISTBOX_MAX_HEIGHT_PX } } }}`
 *    - dieselbe Kappe an mehreren Listen, damit sie gleich hoch stehen
 *
 *  @dontUseFor
 *    - die Hoehe eines FELDES oder KNOPFES — dafuer gibt es `FIELD_HEIGHT_SX`
 *      bzw. `CONTROL_HEIGHT_SX`
 *    - ein `maxHeight` in `vh`/`%`: die Liste soll in px messen, damit zwei
 *      Listen auf denselben Bildschirmen gleich hoch stehen
 *    - `maxHeight` an einem Menue, das gar nicht ueberlappen kann — dann ist die
 *      Kappe eine Zahl ohne Aufgabe
 *
 *  Der Wert 260 ist der Hauswert fuer genau diese Kappe: **fuenf** Stellen im
 *  Baum tragen ihn schon — `LsmInventorySyncPanel.jsx`, `PrivacyErasePanel.jsx`,
 *  `EKTooltip.jsx`, `ScraperList.jsx`, `RMADetail.jsx`. Gemessen 2026-10-10 mit
 *
 *      grep -rn "maxHeight: 260" frontend/src | grep -v theme/tokens.js
 *
 *  Die zweite Pipe nimmt DIESE Zeile heraus: sie traegt das Muster im Fliesstext
 *  und zaehlte sich sonst selbst mit — der Lauf ergaebe 6 statt 5 (Regel 11
 *  §Zeilenkoordinate: „Wer die Zahl hinschreibt, erhoeht sie"). Die Karte K-12
 *  nennt drei davon; gemessen sind es fuenf, und die Zahl, die zaehlt, ist die
 *  gemessene.
 *
 *  🔴 Die Messung (K-12) hat entschieden: der Wert reicht NICHT, um „Cancel"
 *  freizugeben — E-14 (Wert genau EINMAL nachziehen) ist damit gegenstandslos
 *  geblieben, der Rueckfall aus E-8 ist gebaut.
 *
 *  ⚠️ NICHT als Abhilfe gegen eine FLAECHE DARUNTER: die Kappe KUERZT die Liste,
 *  sie schiebt sie nicht hoch — ein Knopf unter dem Feld bleibt verdeckt,
 *  solange die Liste offen ist. Gemessen am `LinkCaseDialog`, 2026-10-10. */
export const LISTBOX_MAX_HEIGHT_PX = 260;

/** Die Breite der BEDIENSPALTEN einer `full`-Liste: Auswahl- und Aktionsspalte
 *  (G1-V, §9 #1 des G1-Plans, 2026-10-13).
 *
 *  🔴 Die zwei Zahlen kommen aus `design/muster/ListenMuster.jsx` (dort standen
 *  sie als lokales Literal), nicht aus einem neuen Entwurf — die Werte sind
 *  **unveraendert 48/140**, nur der ORT wechselt. Der Grund: die Musterroute ist
 *  die Kalibrierflaeche der §15-Proben, und die Listenkarten G1–G15 brauchen
 *  dieselben zwei Breiten. Zwei Literale in einer Musterdatei waeren die Sorte
 *  Zahl, die der naechste Verbraucher abtippt und die dann driftet.
 *
 *  ⚠️ **Beide Werte sind HERGELEITET, nicht gemessen** — und `PW4c` prueft nur
 *  EINE Richtung: es wird rot, wenn die Aktionsspalte zu KLEIN ist
 *  (Ueberlappung), aber nicht, wenn sie unnoetig breit ist. Die Sichtpruefung im
 *  Browser (2560 auf `?breite=full`) muss sie bestaetigen (Gate-Runde 2, R15).
 *
 *  🔴 **§9 #1 datiert festgehalten:** die Praemisse des G0.12-Entscheids („G1
 *  traegt eine Auswahlspalte") ist am Kode widerlegt — `/products` traegt WEDER
 *  eine Auswahl- NOCH eine Aktionsspalte. Der Token entsteht trotzdem, weil die
 *  Musterdatei ihn verbraucht; er ist damit kein toter Wert (Regel 22 misst
 *  `theme/` ohnehin nicht). Die **44-px-Auswahlzelle** wandert auf die erste
 *  Karte mit Auswahlspalte (G4/G5/G6, §13.2).
 *
 *  🔴 **Der `auswahl`-Wert (48) ist am einzigen Verbraucher heute WIRKUNGSLOS**
 *  (gemessen 2026-10-13, G1-V; R15 W-5 / R16 B2): MUI traegt fuer
 *  `padding="checkbox"` die **Zwei-Klassen**-Regel `&.MuiTableCell-paddingCheckbox
 *  { width: 24 }` (`@mui/material/TableCell/TableCell.js:88-95`, Spezifitaet
 *  0,2,0) gegen die EINE `sx`-Klasse (0,1,0) — gerendert sind **24 px**, nicht 48.
 *  Beleg: der Kommentar `ListenMuster.jsx:850-856` und die Probe
 *  `ListenMuster.breite.test.jsx` M.4 (`:148-150`, pinnt die 24 px als Literal).
 *  Behebung auf der ersten Karte **mit** Auswahlspalte (G1-Plan §9 #1); Akte
 *  `docs/backlog-auto-repair/2026-10-13-auswahlspalte-48-wirkt-nicht-mui-24.md`. */
export const BEDIENBREITE = { auswahl: 48, aktion: 140 };

/** Die feste **Bildspalte** einer `full`-Liste — 40 px `ProductThumb size={40}` +
 *  2 × 8 px Zellpolster (`SP.tight`) = **56**. Sie steht NEBEN dem Spaltenbudget,
 *  weil ein Vorschaubild keine `COLUMN_MAX`-Art hat (Grill W12); die Zelle MUSS
 *  darum `px: SP.tight` ausdrücklich setzen — MUI `size="small"` trägt sonst
 *  horizontal 16 px je Seite, und das 40-px-Bild läge in einem 24-px-Kasten
 *  (R15 W3 der Runde 1, R16 C1 der Runde 3).
 *
 *  🔴 **Eine Quelle für zwei Listen und die Probe** (G3b-Nachbesserung 3, R15 W3):
 *  vorher stand `56` dreimal als Literal (`SecondLifeBestand.jsx`,
 *  `OpenCases.jsx`, `tests/listen/breite.spec.ts`). `theme/` ist von Regel 22
 *  ausgenommen — die Zahl darf hier stehen. */
export const BILD_SPALTE_PX = 56;

/** T2 — Glass pill: horizontal filter chips, segment selectors.
 *  Active state: override bgcolor to primary.main + color to primary.contrastText.
 *  Höhe über `CONTROL_HEIGHT_SX` (vor 2026-10-06 flach 44 auf jeder Breite). */
export const GLASS_PILL_SX = {
  borderRadius: 99,
  px: 2,
  ...CONTROL_HEIGHT_SX,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: 'var(--glass-bg)',
  backdropFilter: 'saturate(160%) blur(10px)',
  WebkitBackdropFilter: 'saturate(160%) blur(10px)',
  border: '1px solid var(--glass-border)',
  boxShadow: 'var(--glass-shadow)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  userSelect: 'none',
  // K-T1: `transform` kam in die Liste — ohne ihn springt der Ausschlag
  // statt zu laufen. Die zwei Farbwerte behalten ihre 0.15 s.
  transition: `background-color 0.15s ease, color 0.15s ease, transform ${PRESS_MS}ms ease`,
  // K-T1: der alte Ausschlag (0,94) kommt jetzt aus `PRESS_SCALE`, und das
  // Zeiger-Gatter kam dazu — die Pille drückte bis dahin auf JEDEM Zeiger.
  // 🔴 K-T1-Nachbesserung (2026-10-09): hier stand `...PRESS_ACTIVE_COARSE`,
  // und der zweite `'@media (pointer: coarse)'`-Schlüssel hat den 44-px-Boden
  // aus `CONTROL_HEIGHT_SX` STILL verworfen. Die zusammengesetzte Konstante
  // trägt beides — Boden und Druck.
  ...TOUCH_TARGET_COARSE_ACTIVE,
};

/** T2b — Facetten-Pille: der Ausloeser einer Filter-Facette (BS7,
 *  `desktopForm="pill"` der `BottomSheet*`-Bausteine).
 *
 *  Sie ist die Glaspille in der **Hoehe eines Feldes derselben Zeile** — genau
 *  das ist ihr Zweck und der einzige Unterschied zu `GLASS_PILL_SX`.
 *
 *  🔴 Warum nicht einfach `GLASS_PILL_SX` an der Facette: die traegt
 *  `CONTROL_HEIGHT_SX` (32 px ab `sm`), waehrend ein `size="small"`-Feld
 *  derselben Zeile `FIELD_HEIGHT_SX` traegt (40 px ab `sm`). Eine Facette neben
 *  einem Suchfeld stuende damit 8 px zu tief — N2.4 „eine Hoehe je Zeile"
 *  (F2). Die 40 px sind nicht angenommen, sondern gemessen: Playwright,
 *  1440 px, Manual-Update-Zeile auf `/products/<sku>` (2026-10-08).
 *
 *  🔴 Die Zeiger-Achse wird NEU zusammengesetzt, nicht zweimal gespreadet.
 *  `GLASS_PILL_SX` traegt `CONTROL_HEIGHT_SX` (das bringt
 *  `TOUCH_TARGET_COARSE`) UND danach `TOUCH_TARGET_COARSE_ACTIVE`;
 *  `FIELD_HEIGHT_SX` bringt `TOUCH_TARGET_COARSE` noch einmal mit. Der ZWEITE
 *  `'@media (pointer: coarse)'`-Schluessel im selben Objektliteral ERSETZT den
 *  ersten — deshalb steht `TOUCH_TARGET_COARSE_ACTIVE` als LETZTES: Boden UND
 *  Druck unter EINEM Gatter. Ohne die Zeile verloere die Facette auf dem groben
 *  Zeiger ihren Druck, genau der Fehler, den die K-T1-Nachbesserung an
 *  `GLASS_PILL_SX` behoben hat.
 *
 *  → `sx={{ ...FACET_PILL_SX }}` am Ausloeser der Facette.
 *
 *  *Was diesen Token widerlegt:* eine Browsermessung, in der Facette und Feld
 *  derselben Zeile mehr als 1 px auseinanderliegen. */
export const FACET_PILL_SX = {
  ...GLASS_PILL_SX,
  ...FIELD_HEIGHT_SX,
  ...TOUCH_TARGET_COARSE_ACTIVE,
};

/** Die HOECHSTBREITE des Pillentextes einer Facette (UI2 Teil 10, G0.4 / BS7).
 *
 *  Ohne sie waechst der Text unbegrenzt: die Suche fuellt Z1 (N2.9), die
 *  Facetten stehen rechts daneben — eine Pille in beliebiger Breite verdraengt
 *  sie. Der FELD-Zweig derselben Bausteine kuerzt laengst (`textOverflow:
 *  'ellipsis'`); die Pille tat es bis zur R16-W3-Nachbesserung (2026-10-12)
 *  nicht.
 *
 *  Der Wert deckt den laengsten realistischen Facettentext („Status: Awaiting
 *  return", „Issued: 01.10.2026 – 11.10.2026") und bleibt deutlich unter der
 *  Breite, die eine einzelne Facette in Z1 einnehmen darf.
 *
 *  → `sx={{ maxWidth: FACET_PILL_MAX_WIDTH_PX, overflow: 'hidden',
 *     textOverflow: 'ellipsis' }}` am Pillentext.
 *
 *  *Was diesen Token widerlegt:* eine Browsermessung, in der die gekappte
 *  Pille ihren Wert nicht mehr lesbar zeigt oder in der Z1 ohne die Kappung
 *  nicht ueberlaeuft. */
export const FACET_PILL_MAX_WIDTH_PX = 240;

/** T3 — Round icon button: search trigger, sort, more-options, close.
 *  40px glass circle. Proven in MobileBottomNav as FloatingRoundButton. */
export const ROUND_ICON_BTN_SX = {
  width: 40,
  height: 40,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  bgcolor: 'var(--glass-bg)',
  backdropFilter: 'saturate(160%) blur(10px)',
  WebkitBackdropFilter: 'saturate(160%) blur(10px)',
  border: '1px solid var(--glass-border)',
  boxShadow: 'var(--glass-shadow)',
  cursor: 'pointer',
  // 🔴 R16 W1 (2026-10-09): `sx` schlaegt das Theme — der Farbteil steht deshalb
  // SELBST hier (wie `mitDruck`): `background-color` (MUI's `shortest`) + `PRESS_TRANSITION`.
  transition: `background-color ${MUI_SHORTEST_MS}ms ease, ${PRESS_TRANSITION}`,
  // K-T1: der alte Ausschlag (0,88) kommt jetzt aus `PRESS_SCALE`, Gatter wie
  // an der Glaspille.
  ...PRESS_ACTIVE_COARSE,
};

/** 🔴 M2 (2026-10-16) — **MATERIAL**: Toenung + Unscharfe + die zwei
 *  Rueckfallzweige. Die **Form** liegt NICHT hier — sie sitzt an der Flaeche
 *  selbst (`SUCHE_KARTE_RADIUS` am Bereichsblatt).
 *
 *  🔴 **Seit Suche mobil v2 Stufe 1 (2026-10-31) hat er ZWEI Leser:** den
 *  **Klebekopf** (`suche-gruppenkopf`) und das **Bereichsblatt**; ab Stufe 2 nur
 *  das Blatt (der Kopf faellt mit den Chips, N1). Panel, Kapsel und Trefferkarte
 *  tragen kein Glas mehr (Plan §9.1/§9.5) — der Grund ist `background.default`.
 *
 *  🔴 M3b-Nachbesserung 3 (2026-10-19, R15-W4): `MOBILE_SEARCH_PANEL_SX` ist
 *  ENTFERNT. Es hatte keinen Produktions-Verbraucher mehr (die Schale sitzt
 *  seit M3b auf Karte und Kapsel) und hielt als Einziges `SHADOWS.searchPanelUp`
 *  am Leben — einen Schatten, den keine gerenderte Flaeche mehr liest.
 *
 *  @useWhen
 *    - Eine Flaeche soll das Such-Glas tragen, steht aber NICHT am Bildschirmrand
 *      (Klebe-Gruppenkopf mitten im Scroller) → **nur** dieses Token
 *
 *  @dontUseFor
 *    - Einen Klebekopf MIT Lichtkante oder Schatten — genau die vier
 *      Schalen-Eigenschaften, die Grill-R2 B1 als Fehler gemeldet hat
 *    - Per-Zeilen-Flaechen in langen Listen (Hauswarnung oben: GPU thrash)
 *
 *  🔴 **Die Toenung ist hergeleitet, nicht gewaehlt** (Plan §4): der bindende
 *  Fall ist der HELLE Modus ueber dunklem Inhalt, er gibt die Untergrenze
 *  α ≥ ~0,78. **0,80** haelt in beiden Modi mit Abstand und ist deutlich unter
 *  den 0,97, die der Owner beanstandet hat (20 % Durchlass gegen 3 %).
 *
 *  🔴 **Zwei Rueckfallzweige, beide NEU im Haus, und sie liegen im MATERIAL,
 *  nicht in der Schale** — so bekommt der Klebekopf sie mit und ist nicht die
 *  einzige Flaeche, die bei „Transparenz reduzieren" durchsichtig bliebe:
 *    - `prefers-reduced-transparency: reduce` → **deckend**, ohne Unscharfe
 *    - `@supports not (…)` → **deckend**. Ohne Unscharfe waere eine
 *      durchscheinende Flaeche UNLESBARER als eine deckende: der Text laege
 *      ueber scharfen, hellen Konturen. Beide Schreibweisen werden geprueft —
 *      Safari kennt bis 18 nur `-webkit-backdrop-filter`, ein Test allein auf
 *      die unpraefixierte Eigenschaft waere dort **falsch-negativ**.
 *
 *  ⚠️ **Der Preis, benannt:** ein `backdrop-filter` ueber einem Scroller wird
 *  bei JEDEM Scrollbild neu komponiert (Hauswarnung oben, „GPU thrash"). Er ist
 *  begrenzt: **eine** Flaeche plus je Gruppe ein Kopf, nicht je Zeile. Der
 *  Rueckfall ist **nicht** die deckende Flaeche (das ist der beanstandete
 *  Zustand) — die Richtung steht in Plan §4. */
export const MOBILE_SEARCH_GLASS_SX = {
  bgcolor: (t) => alpha(t.palette.background.default, 0.80),
  backdropFilter: 'blur(24px) saturate(180%)',
  WebkitBackdropFilter: 'blur(24px) saturate(180%)',
  '@media (prefers-reduced-transparency: reduce)': {
    bgcolor: 'background.default',
    backdropFilter: 'none',
    WebkitBackdropFilter: 'none',
  },
  '@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))': {
    bgcolor: 'background.default',
  },
};

/** 🔴 M3b (2026-10-19) — der Radius der **Trefferkarte UND des Bereichsblatts**.
 *  EIN Token fuer die zwei Flaechen, die dieselbe Glaskarte sein sollen (§B2.7);
 *  das Blatt kopierte die 20 px bis zur Nachbesserung 1 als Literal (R16-W5).
 *
 *  🔴 **Mit Einheit** (`'20px'`), aus demselben Grund wie `SUCHE_FELD_RADIUS`:
 *  eine nackte Zahl in `sx` ist ein Vielfaches von `theme.shape.borderRadius`
 *  (6) und ergaebe 120 px. */
export const SUCHE_KARTE_RADIUS = '20px';


/** 🔴 M3b (2026-10-19) — der **seitliche Abstand des Bereichsblatts** zur
 *  Saeulenkante (12 px).
 *
 *  Bis v2 war er `SUCHLEISTE_POLSTER` (dieselbe Zahl, drei Rollen: Kapselrand,
 *  Abstand Karte ↔ Kapsel, Blattrand). Seit Suche mobil v2 Stufe 1 gibt es
 *  Kapsel und Karte nicht mehr — der Token bleibt fuer die EINE verbliebene
 *  Rolle (Blattseitenrand, `SearchBereichWahl.jsx`) und ist deshalb **neu
 *  basiert**: eine eigene Zahl statt einer Ableitung von einem geloeschten
 *  Token (Plan §9.5, W4). */
export const SUCHE_ABSTAND = 1.5;

/** Die px-Form von `SUCHE_ABSTAND` fuer die CSS-Variable `--suche-abstand`
 *  (Hausform der Ableitung, s. `SUCHE_PANEL_MINDESTHOEHE_PX`). */
export const SUCHE_ABSTAND_PX = SUCHE_ABSTAND * 8;

/** 🔴 M3b (2026-10-19) — der Ueberstand der Schicht (§B2.2).
 *
 *  ⚠️ **HYPOTHESE, kein Beleg (W11):** ob `inset: 0` bei layout-relativem
 *  `fixed` Luecken laesst, ist nicht gemessen. Der Ueberstand kostet nichts und
 *  schadet nicht; `M3.3` misst die WIRKUNG (unter der Kapsel liegt Overlay),
 *  nicht den Wert. Ist die Hypothese falsch, kann er auf 0 — ohne dass eine
 *  Zusicherung faellt. Er ist KEINE Mutationsgroesse (Grill R2 B4). */
export const SUCHE_SCHICHT_UEBERSTAND_PX = 240;

/** Der Rueckfall der CSS-Variable `--suche-sicher-oben` (§B2.3). Kode und Probe
 *  teilen EINEN Namen. */
export const SUCHE_SICHER_OBEN = 'env(safe-area-inset-top, 0px)';

/** Die Zeilenhoehe im mobilen Bereichsblatt (§B2.7). 48 px, ueber dem
 *  Hausboden 44 (`TOUCH_TARGET_COARSE`); der Auftrag nennt „≥ 48 px". */
export const SUCHBEREICH_BLATT_ZEILE_PX = 48;

/** 🔴 R15-W3 (Nachbesserung 2, 2026-10-19) — **wann gilt die Tastatur als
 *  offen**. Zwei Bedingungen, gemeinsam:
 *
 *  1. Der Layout-Viewport ist mindestens `SUCHE_TASTATUR_SCHWELLE_PX` hoeher als
 *     der Visual Viewport. Ohne Schwelle las ein **eingerasteter**
 *     Leisten-/Adressleisten-Zustand (ein paar Pixel) als „Tastatur offen" und
 *     verschoeb `--suche-unten` auf den blossen Abstand — der Geraeteboden
 *     (Home-Indikator) verlor seinen Schutz.
 *  2. `visualViewport.scale < SUCHE_TASTATUR_SCALE_MAX`. Beim **Pinch-Zoom**
 *     schrumpft der Visual Viewport ebenfalls; ohne diese Bedingung galt ein
 *     Zoom als Tastatur und die Kapsel rutschte nach unten.
 *
 *  Die 150 px liegen deutlich ueber dem, was eine ein-/ausfahrende Leiste
 *  bewegt, und deutlich unter einer Tastatur (≈ 300 px). */
export const SUCHE_TASTATUR_SCHWELLE_PX = 150;

/** Die zweite Bedingung aus R15-W3: ab `scale >= 1.05` ist es ein Zoom, keine
 *  Tastatur. `1` ist der Normalzustand; die 5 % sind die Rundungstoleranz
 *  zwischen Geraeten. */
export const SUCHE_TASTATUR_SCALE_MAX = 1.05;

/** 🔴 M2 §7 (2026-10-16) — die **Pillenform der Geruest-Balken** im mobilen
 *  Such-Panel. Das Geruest bildet die Anatomie der Trefferzeile ab, und die
 *  Zeile traegt zwei `Chip`s (Art/Status, 20 px hoch, volle Rundung).
 *
 *  🔴 **99 ist der HAUSWERT der Glaspille** (`GLASS_PILL_SX`) — dieselbe Form,
 *  ein Wert. Und er steht hier als **Token**, weil ein rohes
 *  `borderRadius: 999` im Bauteil die `check_ui_tokens`-Grundlinie steigen
 *  liesse: die Hausregel ist „Werte kommen aus Tokens", und eine Ausnahme
 *  „nur für ein Geruest" wäre genau die Sorte Ausnahme, die den Waechter
 *  erodiert. Zwei Verbraucher (die zwei Balken), eine Quelle. */
export const SKELETON_PILL_RADIUS = 99;

/**
 * EASE — die vier Kurven, die das Haus benutzt.
 *
 * Vorher standen sechs `cubic-bezier`-Varianten im Baum, von denen drei
 * dasselbe meinten (`0.0,0,0.2,1` und `0.0,0.0,0.2,1`), plus 74-mal das
 * nichtssagende `ease` des Browsers. Eine Kurve sagt, WIE sich etwas bewegt —
 * ohne Namen ist sie nicht wiedererkennbar und wird beim naechsten Mal neu
 * erfunden.
 *
 * Die Richtung ist die Aussage, nicht die Zahl:
 *   decelerate — etwas KOMMT AN. Schnell rein, weich auslaufen.
 *   accelerate — etwas GEHT WEG. Weich anfahren, schnell raus.
 *   standard   — symmetrisch, fuer Zustandswechsel ohne Richtung (Farbe, Groesse).
 *   drawer     — starkes Auslaufen, fuer grosse Flaechen (Sheets, Schubladen).
 */
export const EASE = {
  decelerate: 'cubic-bezier(0.0, 0, 0.2, 1)',
  accelerate: 'cubic-bezier(0.4, 0, 1, 1)',
  standard:   'cubic-bezier(0.4, 0, 0.2, 1)',
  drawer:     'cubic-bezier(0.22, 1, 0.36, 1)',
};

/**
 * DUR — die Dauern, nach Entfernung statt nach Gefuehl.
 *
 * 300 ms ist die Obergrenze fuer einen UI-Uebergang; darueber wirkt die
 * Oberflaeche traege, egal wie schoen die Kurve ist. Endlosschleifen
 * (Spinner, Status-Pulse) fallen NICHT darunter — sie sollen laufen, solange
 * etwas laeuft.
 */
export const DUR = {
  micro: 100,   // Farbwechsel, Hover
  ui:    200,   // Standarduebergang
  panel: 300,   // groessere Flaechen, Obergrenze
  // Knopfdruck (K-T1). Liest `PRESS_MS` von oben — EINE Zahl, zwei Namen:
  // `PRESS_TRANSITION` baut daraus den Uebergang der Druckrueckmeldung, und
  // `MobileBottomNav` liest `DUR.press` direkt. Hier steht sie, damit die
  // Haus-Dauern vollstaendig an EINER Stelle stehen.
  press: PRESS_MS,
};

// ── Warteschlangen-Uebergaenge ─────────────────────────────────
/** T4 — Bottom sheet transition: spread onto every SwipeableDrawer to get correct
 *  physics. MUI defaults (225ms/195ms, "sharp" exit) feel cheap — too fast and
 *  wrong easing direction on close. Enter: decelerate 320ms. Exit: accelerate 220ms. */
export const BOTTOM_SHEET_TRANSITION = {
  transitionDuration: { enter: 320, exit: 220 },
  SlideProps: {
    // W-59: die Kurven tragen jetzt Namen. Sie standen hier schon als Strings,
    // waren aber nur HIER wiedererkennbar — anderswo wurden dieselben Werte
    // neu getippt, in zwei Schreibweisen.
    easing: {
      enter: EASE.decelerate,
      exit:  EASE.accelerate,
    },
  },
};


// ── Hover nur, wo es Hover gibt ────────────────────────────────
/**
 * hoverOnly — verpackt Hover-Stile in eine Faehigkeitsabfrage.
 *
 * Auf einem Touchgeraet gibt es kein Hover. Der Browser wendet `:hover` beim
 * Tippen trotzdem an und LAESST ES STEHEN, bis man woanders hintippt. Eine
 * Zeile, die sich beim Antippen hervorhebt, bleibt also markiert — sie sieht
 * ausgewaehlt aus, obwohl sie es nicht ist.
 *
 * Am 2026-09-28 gemessen: 144 `:hover` in 73 Dateien, davon 68 mit einem
 * Hintergrundwechsel — die Sorte, die sichtbar klebt. Nur zwei Dateien hatten
 * die Abfrage. MUI gated seine EIGENEN Hover (`MuiTableRow`) bereits selbst;
 * betroffen sind die eigenen `sx`-Stile.
 *
 * Verwendung:
 *   sx={{ ...hoverOnly({ bgcolor: 'action.hover' }) }}
 *
 * `(hover: hover)` allein genuegt nicht: ein Geraet mit Touchscreen UND Maus
 * meldet `hover: hover`, und dort soll der Hover bleiben. `pointer: fine`
 * schliesst den Fall aus, in dem der primaere Zeiger ungenau ist. 🔴 Eine FUNKTION und kein `const`: `HOVER_LIFT_SX` (Z. 342) ruft sie beim Modulladen — eine `const`-Pfeilfunktion waere dort ein TDZ-Fehler, eine Funktionsdeklaration ist hochgezogen.
 */
export function hoverOnly(styles) {
  return {
    '@media (hover: hover) and (pointer: fine)': { '&:hover': styles },
  };
}

// ── Bewegung: Kurven und Dauern ────────────────────────────────

/**
 * TOAST_DURATION — die zwei Haus-Dauern fuer sonner-Toasts (M-15, K-F3).
 *
 *   short  4000 ms — Erfolg und Info: der Nutzer muss nichts tun, der Hinweis
 *                    darf weggehen.
 *   long   8000 ms — Fehler UND der Toast MIT einer Aktion, fuer den die Dauer
 *                    eingefuehrt wurde. Ein Fehler will gelesen werden, und eine
 *                    Ruecknahme braucht Lese- UND Reaktionszeit; beides ist ohne
 *                    Handlung des Nutzers weg.
 *
 * 🔴 Das sind die Haus-Dauern fuer die in K-F3 umgestellten Stellen — NICHT die
 *    einzige Quelle im Baum. Ein Literal an einer NEUEN Aufrufstelle bleibt
 *    falsch (es waere die zweite Quelle); die drei BESTEHENDEN Ausnahmen sind
 *    benannt und bleiben, wo sie sind.
 *
 * ⚠️ Die drei Stellen mit eigener Dauer, und ihr Grund:
 *
 *   1. `components/products/SparePartsSection.jsx` — `duration: 5000` am
 *      Undo-Toast eines KNOPF-Ausloesers. Wer den Knopf drueckt, hat die
 *      Handlung gerade gewollt und weiss, was er rueckgaengig machen will; ein
 *      Wisch loest leichter versehentlich aus. Deshalb die KURZE Dauer am Knopf
 *      und die lange am Wisch (`SWIPE_UNDO_MS`). NICHT gezogen — die Datei steht
 *      in keiner K-F3-Zeile.
 *   2. `ProtectedRoute.jsx` — `duration: 6000` am „Session expired"-Fehler.
 *      NICHT gezogen: ausserhalb des Umfangs von K-F3.
 *   3. `utils/mindestpreisWarnung.js` (`anzeigeDauer()`) — 14000/8000, eine
 *      begruendete dritte Quelle; gelesen an `ProductDetail.jsx` und
 *      `components/MarketPricingOverview.jsx`. NICHT gezogen.
 *
 *    Wer eine vierte findet, nennt sie hier. Die Vollstaendigkeit ist mit DIESEM
 *    Befehl geprueft und nicht mit einer Ziffer, die von selbst altert:
 *
 *      grep -rnE "duration: ?[0-9]|duration: [A-Za-z]" frontend/src \
 *        --include='*.js' --include='*.jsx' | grep -v test
 *
 *    Er findet auch die `transition={{ duration }}`-Faelle von framer-motion
 *    (Zahlen unter 1 s) — die sind Bewegung, keine Anzeigedauer, und gehoeren
 *    nicht hierher.
 *
 * ⚠️ Die letzten zwei MUI-`Snackbar`-Dauern sind damit gezogen (Fehler-Toast in
 *    `NotificationCenter.jsx`, Undo-Toast in `rma/RMADetail.jsx`).
 *    `admin/v2/JobsList.jsx` ist die EINE verbliebene `Snackbar` — fester
 *    Bildschirmwinkel oben rechts und strukturierter Inhalt (Titel + Dauer/
 *    Fehlerrest) statt einer Zeile Text; sie nimmt dieselben zwei Werte.
 */
export const TOAST_DURATION = { short: 4000, long: 8000 };

/**
 * SWIPE_UNDO_MS — wie lange der Widerruf eines WISCHES steht (G7).
 *
 * 🔴 Seit K-F3 (2026-10-09) ist das ein NAME fuer `TOAST_DURATION.long`, kein
 *    eigenes Literal. Die Begruendung fuer die LANGE Dauer bleibt seine eigene:
 *    ein Wisch loest leichter versehentlich aus als ein Knopf, und der Nutzer
 *    muss den Hinweis erst SEHEN, bevor er ihn treffen kann — die 8 s sind die
 *    Zeit, die ein Blick auf das Telefon plus ein Griff brauchen.
 *
 *    ⚠️ Der Gegenfall steht namentlich in `TOAST_DURATION`: der KNOPF-Ausloeser
 *    in `components/products/SparePartsSection.jsx` traegt die KURZE Dauer.
 *    Hier stand bis zum 2026-10-09 „8000 ms sind die Dauer JEDES Toasts MIT
 *    einer Aktion" — zu weit, seit der Knopf-Fall benannt ist (R15/R16 B1).
 *
 *    ⚠️ Der Beleg, der hier bis zum 2026-10-09 stand, ist GEFALLEN (R16 C2):
 *    `NotificationCenter.jsx:612` trug seinen 5000er an einem WISCH-Ausloeser,
 *    nicht an einem Knopf, und stuetze den Satz nicht, den er belegen sollte.
 *    Der Pfad war zudem unvollstaendig geschrieben (`products/…` statt
 *    `components/products/…`).
 *
 * 🔴 Er gehoert an DIESE Stelle und nicht neben `MOBILE_BOTTOM_NAV_CLEARANCE`
 *    (`:542-546`): dort legt der Schwester-PR `MOBILE_APP_BAR_CLEARANCE` an,
 *    und zwei Aenderungen im selben Hunk kollidieren beim Merge.
 *
 * *Widerleger:* eine Messung, in der 8 s als zu lang empfunden werden (der
 * Hinweis steht dem naechsten Griff im Weg) → auf `TOAST_DURATION.short` zurueck.
 */
export const SWIPE_UNDO_MS = TOAST_DURATION.long;

// ── V2 — Case-System Design Language (additive, candidate) ─────
/**
 * V2 — Design-Language v2 candidate token set (case-system UI).
 *
 * @description
 *   Additive-only token group introduced for the case-workflow-engine
 *   Phase-1 mockup conversion (six approved surfaces). Origin:
 *   docs/concepts/case-workflow-engine/ux/design-language-proposal.md
 *   (owner-signal 2026-07-30: "Die Designsprache ist deutlich klarer
 *   als unser bisheriges Design"). This is a CANDIDATE set surfaced
 *   for a later, explicit app-wide adoption decision — see that doc's
 *   "Adoptionspfad" section. It does NOT replace or alter FS, SP,
 *   SHADOWS, PANEL_RADIUS, BUTTON_RADIUS, PANEL_SX, or GLASS_PILL_SX
 *   above — every existing screen keeps rendering with those,
 *   unchanged, until a screen explicitly opts into V2.
 *
 * @useWhen
 *   - Building one of the six Phase-1 case-system mockup surfaces
 *   - A component explicitly opts into the case-system design language
 *
 * @dontUseFor
 *   - Any existing/untouched screen — those keep PANEL_RADIUS/SHADOWS
 *   - Renaming or replacing an existing export — V2 is additive only
 *
 * @related
 *   - PANEL_RADIUS / SHADOWS — the v1 tokens V2 sits alongside, not over
 *   - theme.js `palette.agent` — MUI custom palette color consuming
 *     `V2.agent` / `V2.agentDark`
 *
 * @reuseHints
 *   - RMA-Übersicht is the proposal's first candidate for app-wide
 *     adoption if the post-Phase-1 owner decision is "yes"
 */
export const V2 = {
  // Agent — semantic role for anything agent-generated (chips, timeline,
  // logs, cards). AA 5.2:1 verified on agentSoft per the proposal.
  agent:            '#7c3aed',
  agentSoft:        '#f5f3ff',
  agentSoft100:     '#ede9fe',
  agentHover:       '#6d31d4',
  // Dark-mode pendants (only `agent` has a defined dark pendant so far —
  // see proposal "Offen" section for the rest).
  // 🔴 RAISED 2026-08-05 (Owner-Entscheid E4), violet-400 → violet-300. The old
  // `#a78bfa` measured 4.35:1 on its own soft fill over `paper` #1e293b, under
  // the 4.5:1 AA floor for normal text. See the canon table's E4 correction
  // block for the reasoning and the full re-measurement.
  agentDark:        '#c4b5fd',
  agentSoftDark:    'rgba(196,181,253,.13)',
  agentSoft100Dark: 'rgba(196,181,253,.32)',

  // Semantic soft pairs (text token tuned one step darker than Tailwind
  // default so it stays ≥4.7:1 on its own -soft fill) + -100 full-border
  // tones (used with the anti-pattern rule: soft fill + full -100 border,
  // never a left accent bar on a rounded surface).
  // ── Semantic quartet, both modes ─────────────────────────────
  // Completed 2026-08-05 (P1/PR-A). Until then only `err` had dark pendants
  // and `ok`/`warn`/`info` had none at all, which is why SituationHeader.jsx
  // `toneSx` reaches for hex literals like `rgba(251,191,36,0.12)`: there was
  // no token to reach for. A shared Pill could not have rendered `tone="ok"`
  // in dark mode without carrying that literal into components/common/, and
  // the SOT forbids hex literals in border declarations.
  // Values are byte-copies of the Dark-Mode canon table in
  // docs/concepts/case-workflow-engine/ux/design-language-proposal.md:210-224.
  okText:    '#15803d',
  okSoft:    '#ecfdf3',
  ok100:     '#bbf7d0',
  okTextDark:  '#4ade80',
  okSoftDark:  'rgba(74,222,128,.13)',
  ok100Dark:   'rgba(74,222,128,.35)',
  warnText:  '#b45309',
  warnSoft:  '#fffbeb',
  warn100:   '#fde68a',
  warnTextDark: '#fbbf24',
  warnSoftDark: 'rgba(251,191,36,.13)',
  warn100Dark:  'rgba(251,191,36,.35)',
  errText:   '#b91c1c',
  errSoft:   '#fef2f2',
  err100:    '#fecaca',
  // 🔴 RAISED 2026-08-05 (Owner-Entscheid E4), red-400 → red-300. Same finding
  // as `agentDark`: `#f87171` measured 4.41:1 on its own soft fill over `paper`.
  errTextDark: '#fca5a5',
  errSoftDark: 'rgba(252,165,165,.13)',
  err100Dark:  'rgba(252,165,165,.35)',
  infoText:  '#2563eb',
  infoSoft:  '#eff6ff',
  info100:   '#bfdbfe',
  infoTextDark: '#60a5fa',
  infoSoftDark: 'rgba(96,165,250,.13)',
  info100Dark:  'rgba(96,165,250,.35)',
  // ── Brand (= the canon's `primary` row), both modes ───────────
  // Added 2026-08-05 (PR-A gate fixes). The Pill tone `brand` was struck from
  // the P1 plan as "ein Ton ohne Token in beiden Modi". That was false: the
  // canon table (design-language-proposal.md:217) carries
  // `primary` / `-soft` / `-100` in BOTH modes. What was actually missing were
  // these six token members — PR-A lifted ok/warn/err/info out of that table
  // and skipped the brand row. Values are byte-copies of that row.
  // `primaryText` / `primaryTextDark` are the SAME values theme.js already
  // sets on `palette.primary.main`; theme.js now reads them from here so the
  // hex exists once. A test pins `palette.primary.main === palette.v2.brand.main`
  // in both modes, so the two cannot drift apart silently.
  primaryText:     '#2c387e',
  primarySoft:     '#eef0fa',
  primary100:      '#dfe3f5',
  primaryTextDark: '#93c5fd',
  primarySoftDark: 'rgba(147,197,253,.13)',
  primary100Dark:  'rgba(147,197,253,.30)',

  // Ink alpha scale — text-on-soft-surface hierarchy. Rule: load-bearing
  // info (provenance, sources) never below ink70; ink45 is metadata-only.
  //
  // ⚠️ The light values are alpha over #0f172a and are UNREADABLE on a dark
  // surface — MoneyGuardsCard.jsx:130-133 documents the workaround it had to
  // invent before the dark siblings existed. Use `inkFor(mode)` below rather
  // than picking a member by hand; a rule like "provenance never below ink70"
  // is only enforceable if ink70 exists in both modes.
  // Die Basis der Skala, benannt. Sie stand bis 2026-10-01 nur im Kommentar
  // darueber ("alpha over #0f172a") und in jeder der drei Stufen, aber nicht als
  // eigener Wert. Folge: fuer Ueberschriften und tragenden Text gab es kein Ziel
  // — die naechste Stufe waere ink70 gewesen, und das haette jeden Titel heller
  // gemacht. Gemessen: 33 Stellen trugen #0f172a, die meisten davon Titel.
  ink: '#0f172a',
  inkDark: '#e2e8f0',

  ink70: 'rgba(15,23,42,.70)',
  ink45: 'rgba(15,23,42,.45)',
  ink25: 'rgba(15,23,42,.25)',
  ink70Dark: 'rgba(226,232,240,.72)',
  ink45Dark: 'rgba(226,232,240,.50)',
  ink25Dark: 'rgba(226,232,240,.22)',

  // Second surface tier — Card (16px) sits inside Panel (18px effective),
  // additive to the existing single-tier PANEL_SX/PANEL_RADIUS.
  cardRadius: 16,

  // Unified row-hover tint (replaces 3 near-white one-offs found across
  // the new Phase-1 surfaces during suite review).
  hoverTint: '#fafbfd',

  // ── Markt-Farben der Berichts-Charts ─────────────────────────
  // Regel 22 §6 verlangt eine Chart-Gruppe (`palette.chart`); sie existiert noch
  // nicht, Chart-Farben sind bis dahin die eine erlaubte Ausnahme. Sie stehen
  // hier EINMAL statt als Literale in `VendorReportView.jsx` — und ausdruecklich
  // NICHT als semantische Rollen: `V2.agent` bedeutet in dieser Anwendung
  // „vom Agenten erzeugt"; als Marktfarbe fuer Frankreich war das eine falsche
  // Aussage (R16-B-W4, 2026-10-04). Die Werte sind unveraendert gegenueber dem
  // Vorzustand — der Umzug ist ohne sichtbare Aenderung.
  marketChart: {
    DE: '#2563eb',
    AT: '#b45309',
    CH: '#15803d',
    FR: '#7c3aed',
    CZ: '#0891b2',
    DK: '#b91c1c',
  },

  // `wash` — the canon's neutral surface for plain chips, aging tracks and
  // segmented controls (design-language-proposal.md:224). Added 2026-08-05:
  // it was skipped when the semantic quartet was completed, and two shared
  // components promptly hardcoded two DIFFERENT versions of it — Pill used
  // the canon values inline, AgingBar carried AgingTile's older
  // rgba(15,23,42,.06) track. Same role, two pixels. That is precisely the
  // drift the token slice exists to stop.
  wash:     '#f1f5f9',
  washDark: 'rgba(226,232,240,.08)',

  // `washSelected` — the SELECTED step of `wash`, for a plain Pill acting as a
  // filter facet. Added 2026-08-09 for Owner-Entscheid 5, which was raised
  // because the selected state moved only `borderColor`, measured 1.31:1
  // (neutral) / 1.12:1 (brand).
  //
  // 🔴 MEASURED, do not restate as a WCAG pass. The FILL step alone is small:
  //     wash → washSelected      1.13:1 light · 1.27:1 dark
  // It is the SECOND cue, exactly as the owner's "zusätzlich" says. The cue that
  // actually carries WCAG 1.4.11's 3:1 is the BORDER, which `Pill` now paints in
  // `ink70` (plain) or the tone's `main`:
  //     ink70 on washSelected    6.00:1 light · 4.81:1 dark
  //     main  on soft (toned)    4.75:1 light · 4.59:1 dark  (worst tone: info)
  //
  // A single-channel fill reaching 3:1 would need a mid-tone surface with
  // inverted text and would contradict the canon's restraint rule
  // (design-language-proposal.md, Muster 5 — Fläche bleibt soft).
  //
  // An earlier draft of this comment claimed the border "carries the 3:1"
  // while `Pill` still used MUI's stock `divider` (1.31:1) — asserted, never
  // measured. Both gates caught it. The numbers above are computed from these
  // token values composited over `background.paper`.
  washSelected:     '#e2e8f0',
  washSelectedDark: 'rgba(226,232,240,.16)',

  // `pageBg` — die deckende Seitenfläche (Canvas) der öffentlichen
  // Vendor-Report-Ansicht (`VendorReportView.jsx`, Route
  // `/vendor-reports/public/:reportId`). Added 2026-10-05 (R16-A-W-A1).
  //
  // 🔴 Warum ein EIGENER Wert und nicht `wash`: die Seitenfläche war vor PR 5 das
  // Literal `#dde3ee`, wurde dort erst `V2.ink25` (Alpha — die Seitenfarbe hing
  // durch, was dahinter lag) und dann `V2.wash`. `wash` ist aber im SELBEN
  // Dokument die Chip- und Kontrollfüllung (`VendorReportView.jsx:636/656/1199/1418`)
  // — eine Chip-Fläche in der Farbe der Seitenfläche liest sich nur noch über
  // ihren Rand. Zwei Rollen auf einem Wert sind genau die Kollision, gegen die
  // diese Token-Schicht existiert. `pageBg` stellt zugleich den Ton VOR dem PR
  // wieder her; die Rücknahme bewegt damit nichts Sichtbares ausser der Farbe,
  // die sie zurückholt.
  //
  // Kein Dark-Sibling: die öffentliche Berichtsansicht ist hell-only (sie
  // hardcodet `V2.ink` und die übrigen hellen Werte).
  pageBg:     '#dde3ee',

  // ✅ AA note, RESOLVED 2026-08-05 (Owner-Entscheid E4). This block used to
  // record a live defect: `errText`/`agent` on their OWN dark soft fill cleared
  // 4.5:1 over the app background (#0f172a) but NOT over a Paper surface
  // (#1e293b) — err 4.41:1, agent 4.35:1, against 4.5 for normal text. Every
  // panel in the app is Paper, so `<Pill tone="err">` on a card was an ordinary
  // call landing under AA. The note "constrain the USE" was a workaround for a
  // token the SOT would not move; the owner moved the token instead.
  //
  // Both dark tones now sit one Tailwind step lighter (red-300 / violet-300) and
  // the whole dark scale clears AA on BOTH surfaces. Measured after the raise:
  //
  //   tone   soft/paper  soft/bg  solid/paper  solid/bg
  //   ok        6.33      7.90       8.40       10.25
  //   warn      6.63      8.28       8.76       10.69
  //   info      4.59      5.69       5.75        7.02
  //   err       5.92      7.38       7.71        9.41   (was 4.41)
  //   agent     5.98      7.46       7.92        9.67   (was 4.35)
  //   brand     6.08      7.59       8.11        9.90
  //
  // `info` at 4.59 is the new thinnest margin and is the one to watch if the
  // soft alpha (.13) is ever tuned. Pinned by a test in v2Bausteine.test.jsx so
  // a future token edit cannot quietly drop a tone back under the floor.
};

/**
 * CARD_SX — die Huelle der drei Karten im Fall-Rail.
 *
 * @description
 *   `EinordnungCard`, `BefundeCard` und `ZusageEinschaetzungCard` trugen bis
 *   zum 2026-10-07 je eine wortgleiche `SHELL_SX`-Kopie. Drei Kopien desselben
 *   Objekts sind eine zu viele: eine Aenderung am Radius oder am Schatten
 *   haette drei Stellen gebraucht, und die vierte Karte waere beim Abschreiben
 *   danebengegangen. Sie steht deshalb hier, neben den anderen geteilten
 *   SX-Objekten.
 *
 *   🔴 Sie ist NICHT `PANEL_SX`: dort `SHADOWS.panel` und `PANEL_RADIUS`, hier
 *   `SHADOWS.card` und `V2.cardRadius`. Zwei Huellen, zwei Gewichte.
 *
 *   🔴 Sie steht hier UNTEN und nicht bei `PANEL_SX` oben, und das ist kein
 *   Geschmack: `V2` wird erst weiter unten im Modul deklariert. Ein
 *   `const CARD_SX = { borderRadius: `${V2.cardRadius}px` }` neben `PANEL_SX`
 *   liefe in eine `ReferenceError` beim Modulladen (temporal dead zone), und
 *   zwar fuer jede Datei, die `tokens` importiert.
 */
export const CARD_SX = {
  borderRadius: `${V2.cardRadius}px`,
  boxShadow: SHADOWS.card,
  p: { xs: SP.card, sm: SP.md },
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
};

/**
 * inkFor — the ink alpha scale for one palette mode.
 *
 * @description
 *   `V2.ink70` and friends are alpha over #0f172a: correct on a light
 *   surface, unreadable on a dark one. Before the dark siblings existed each
 *   consumer invented its own guard (`isDark ? 'text.secondary' : V2.ink70`)
 *   or forgot to — ConversationStream.jsx:345 still reads `V2.ink45` with no
 *   guard at all. This resolver is the one place that knows the pairing.
 *
 * @useWhen
 *   - A component needs ink70/ink45/ink25 and must work in both modes
 *   - Enforcing "load-bearing information never below ink70"
 *
 * @dontUseFor
 *   - Primary body text — that is `text.primary`, not an ink alpha
 *
 * @related
 *   - V2 — the token group this reads from
 *   - v2Palette (theme.js) — the same idea for the semantic tones
 *
 * @props
 *   mode: 'light' | 'dark'  → { ink70, ink45, ink25 }
 */
export const inkFor = (mode) => (mode === 'dark'
  ? { ink70: V2.ink70Dark, ink45: V2.ink45Dark, ink25: V2.ink25Dark }
  : { ink70: V2.ink70,     ink45: V2.ink45,     ink25: V2.ink25 });


// ── Design System Components ───────────────────────────────────
/**
 * Registered design system components.
 *
 * These components consume tokens defined above and establish
 * consistent patterns across all views. Prefer them over building
 * custom one-offs.
 *
 * ┌────────────────────────────────────────────────────────────────────────────┐
 * │ Component          │ Path                               │ When to use       │
 * ├────────────────────┼────────────────────────────────────┼───────────────────┤
 * │ UnderlineTabs      │ components/common/UnderlineTabs    │ Any view with a   │
 * │                    │                                    │ tab row above a   │
 * │                    │                                    │ full-width divider│
 * │                    │                                    │ + animated slide  │
 * │ FilterPillRow      │ components/common/FilterPillRow    │ Horizontal scroll │
 * │                    │                                    │ filter pills      │
 * │ SearchTrigger      │ components/common/SearchTrigger    │ T3 round icon btn │
 * │                    │                                    │ that expands into │
 * │                    │                                    │ search field      │
 * │ EmptyState         │ components/common/EmptyState       │ Empty state with  │
 * │                    │                                    │ a MANDATORY reason│
 * │ ConfidenceBadge    │ rma/components/ConfidenceBadge     │ LLM confidence    │
 * │                    │                                    │ score display     │
 * └────────────────────┴────────────────────────────────────┴───────────────────┘
 */


// ── K7/K9/K-B (UI-Durchgang 2, Teil 6, 2026-10-10) — die Nachzügler ──────────
//
// 🔴 WARUM DIESE VIER AM DATEIENDE STEHEN (K-B, Nachbesserung 2, 2026-10-10):
// sie standen zuerst bei `TOUCH_TARGET_COARSE` bzw. bei `CONTROL_HEIGHT_SX` —
// mitten im Baum. Jede Einfügung schob alles darunter um ihre Zeilenzahl, und
// die Zeiger auf `tokens.js` in `docs/`, `.claude/` und `frontend/src/` brachen
// (zehn von elf wurden nachgezogen, einer blieb offen). Am Dateiende verschiebt
// keine dieser Einfügungen mehr eine Zeile, die vorher schon da war. Der Preis
// ist die Reihenfolge: `TOUCH_TARGET_MIN` steht nicht mehr neben seinen
// Geschwistern `TOUCH_TARGET_COARSE*` — die JSDoc nennt sie namentlich.

/** Der BODEN, den WCAG 2.2 AA **ueberall** verlangt — 24 × 24 px (2.5.8), auf
 *  JEDEM Zeiger. Der Gegenstand ist ein kleines Bedienelement (ein ×, ein
 *  Ausblenden), das sichtbar kleiner bleiben darf als 44 px und dessen
 *  Trefferflaeche trotzdem die Norm traegt.
 *
 *  🔴 Das ist NICHT dieselbe Aussage wie `TOUCH_TARGET_COARSE`: die zwei Normen
 *  sind verschieden, und beide gelten nebeneinander. 24 px sind 2.5.8 (AA,
 *  ueberall); 44 px sind 2.5.5 (AAA) bzw. die iOS-HIG-Empfehlung und die
 *  HAUS-Untergrenze auf grobem Zeiger. Ein Bauteil, das beide tragen muss,
 *  spreadet BEIDE Tokens — `TOUCH_TARGET_MIN` zuerst, `…_COARSE_QUADRAT`
 *  danach (der spaetere Spread gewinnt unter `(pointer: coarse)`).
 *
 *  K9 (UI-Durchgang 2, Teil 6, 2026-10-10): gebaut fuer die Huelle des
 *  Entfernen-Knopfes am `EmailChipField`.
 *
 *  @useWhen
 *    - ein kleines, sichtbar kleines Bedienelement, dessen Trefferflaeche die
 *      WCAG-Untergrenze tragen soll (Entfernen-×, Ausblenden, Schliessen)
 *    - zusammen mit `TOUCH_TARGET_COARSE_QUADRAT`, wenn 44 px dazukommen
 *  @dontUseFor
 *    - ein Knopf, der die Haus-Untergrenze 44 tragen soll → `TOUCH_TARGET_COARSE*`
 *    - eine LAYOUT-Breite (eine Spalte, ein Feld) — das ist keine Trefferflaeche
 *
 *  → `sx={{ p: 0, ...TOUCH_TARGET_MIN, ...TOUCH_TARGET_COARSE_QUADRAT }}` */
export const TOUCH_TARGET_MIN = {
  minWidth: 24,
  minHeight: 24,
};

/** Die SCHMALE KNOPFZEILE — dieselbe Absicht wie `CONTROL_HEIGHT_SX`, aber
 *  **nur `minHeight`**. Ein Knopf, der seine Hoehe NICHT ueber `height` fuehrt
 *  (etwa ein `Button`, dessen Polsterung die Hoehe traegt), bekommt sonst beim
 *  Spread des vollen Tokens eine zweite, konkurrierende Hoehenangabe.
 *
 *  K7 (UI-Durchgang 2, Teil 6, 2026-10-10): bis hierher stand der Wert an jeder
 *  Stelle ROH im `sx` — `minHeight: { xs: 44, sm: 32 }` plus die Zeigerabfrage,
 *  dreimal abgeschrieben. Drei Absichten waren namenlos; das ist die erste
 *  („schmal, Hauswert 32").
 *
 *  🔴 Die Zeigerabfrage gehoert dazu, aus demselben Grund wie bei
 *  `CONTROL_HEIGHT_SX`: `sx` schlaegt den Theme-Boden auf demselben Element,
 *  und ohne `TOUCH_TARGET_COARSE` blieb die Flaeche auf einem Tablet
 *  (≥ 600 px, grober Zeiger) bei 32 px (W-18, 2026-10-07).
 *
 *  @useWhen
 *    - ein Knopf/eine Pille in einer dichten Zeile, 32 px auf feinem Zeiger
 *    - die Hauptaktion einer Karte oder Zeile (`CaseCtaButton`, `MovePillStrip`)
 *  @dontUseFor
 *    - ein Feld derselben Zeile → `FIELD_HEIGHT_SX` (40), sonst stehen Feld und
 *      Knopf auf verschiedenen Hoehen (Regel-14-Grill B1 zu `FIELD_HEIGHT_SX`)
 *    - ein Knopf, der seine Hoehe ueber `height` fuehrt → `CONTROL_HEIGHT_SX`
 *    - die 36er-Variante derselben Absicht → `DIALOG_MIN_HEIGHT_SX`
 *
 *  → `sx={{ borderRadius: 99, ...CONTROL_MIN_HEIGHT_SX }}` */
export const CONTROL_MIN_HEIGHT_SX = {
  minHeight: { xs: 44, sm: 32 },
  ...TOUCH_TARGET_COARSE,
};

/** Die SCHMALE DIALOG-/KARTENZEILE — dieselbe Absicht wie
 *  `CONTROL_MIN_HEIGHT_SX`, mit dem **gemessenen** Hauswert **36**.
 *
 *  K7 (2026-10-10): die zweite der drei namenlosen Absichten. Sie steht an vier
 *  Stellen (Dialogfuss, Karten-Aktionszeile) — ueberall `{ xs: 44, sm: 36 }`
 *  plus Zeigerabfrage, viermal abgeschrieben.
 *
 *  🔴 Warum 36 und nicht 32: die 36 ist an diesen Stellen GEMESSEN und
 *  ausdruecklich so belassen (W-18, 2026-10-07 — dort ging es um den fehlenden
 *  Zeigerboden, nicht um die Zahl). Ein Zusammenlegen auf 32 waere eine
 *  Hoehenaenderung ohne Auftrag; die zwei Werte bleiben darum zwei Namen.
 *
 *  @useWhen
 *    - Aktionsknoepfe in einem `DialogActions`-Fuss
 *    - die Aktionszeile einer Karte (Hauptaktion + Textknopf daneben)
 *  @dontUseFor
 *    - die Knopfzeile einer Seite → `CONTROL_MIN_HEIGHT_SX` (32)
 *    - ein Feld → `FIELD_HEIGHT_SX` (40)
 *
 *  → `sx={{ ...DIALOG_MIN_HEIGHT_SX, borderRadius: 99 }}` */
export const DIALOG_MIN_HEIGHT_SX = {
  minHeight: { xs: 44, sm: 36 },
  ...TOUCH_TARGET_COARSE,
};

/** Die Trefferflaeche des KOPF-BEREICHSWAEHLERS der globalen Suche.
 *
 *  Stufe B (R16-W4, 2026-10-15): der Wert stand als `minHeight: { xs: 44, sm: 24 }`
 *  roh im `sx` von `SearchBereichWahl`. Er ist die DRITTE Auspraegung derselben
 *  Absicht — zwei Achsen, ein Boden — neben `CONTROL_MIN_HEIGHT_SX` (32) und
 *  `TOUCH_TARGET_MIN` (24, flach).
 *
 *  🔴 **Warum 24 und nicht 32.** Die Pille sitzt in einer 64-px-Kopfzeile unmittelbar
 *  neben dem Suchfeld. 32 px machten sie hoeher als das Feld, das sie bedient; die
 *  24 ist der WCAG-2.5.8-Boden (`TOUCH_TARGET_MIN`) und traegt dort, weil das Feld
 *  daneben die Zeilenhoehe ohnehin vorgibt. Unterhalb `sm` steht dieselbe Pille im
 *  Mobil-Panel, und dort ist 44 die Hausform (`TOUCH_TARGET_COARSE`, beide Achsen).
 *
 *  🔴 **Er gehoert auf das Element, das die Flaeche BILDET — nicht auf die Wurzel.**
 *  Bei einem MUI-`Select` ist das `.MuiSelect-select` (der `role="combobox"`-Knoten,
 *  den `boundingBox()` und `getByRole` messen). MUI setzt auf genau diesen Knoten
 *  `min-height: 1.4375em` (`SelectInput.js`, `&.MuiSelect-select`) — **gleiche
 *  Spezifitaet** wie eine `sx`-Regel der Form `'& .MuiSelect-select'`, und die
 *  MUI-Regel kommt spaeter. Gemessen im Browser (2026-10-15): **20,125 px** im Kopf
 *  (14 px Schrift) und **23 px** im Mobil-Panel (16 px) — das `minHeight` griff gar
 *  nicht, die Hoehe war Zeichen fuer Zeichen `1.4375em`. Deshalb traegt die
 *  Verwendung ein verdoppeltes `&` (`.css.css .MuiSelect-select`, eine
 *  Spezifitaetsstufe hoeher, reihenfolgeunabhaengig).
 *
 *  @useWhen
 *    - die Trefferflaeche des Bereichswaehlers in der Suchpille (Kopf und Mobil)
 *  @dontUseFor
 *    - ein Knopf/eine Pille → `CONTROL_MIN_HEIGHT_SX` (32) bzw. `TOUCH_TARGET_MIN` (24)
 *    - ein Feld derselben Zeile → `FIELD_HEIGHT_SX` (40)
 *
 *  → `sx={{ '&&& .MuiSelect-select': { ...SUCHBEREICH_MIN_HEIGHT_SX } }}` */
export const SUCHBEREICH_MIN_HEIGHT_SX = {
  minHeight: { xs: 44, sm: 24 },
  ...TOUCH_TARGET_COARSE,
};

/** Der PFEIL-LAUF des Bereichswaehlers — der Platz, den das Etikett vor dem
 *  Pfeil freilaesst (R15 W-3, gemessen 2026-10-16).
 *
 *  🔴 **Der Befund.** Im Browser BERUEHRTEN sich Etikett und Pfeil: gemessen
 *  `luecke = −1 px` bei „Competitor", „Purchase Order" und „Inbox mail",
 *  `+0,4 px` bei „All" (1440 px, feiner Zeiger). Zwei Ziele, die sich beruehren,
 *  sind EIN Ziel.
 *
 *  🔴 **Und die Ursache war dieselbe Spezifitaetsfalle wie beim `min-height`,
 *  eine Stufe hoeher.** MUI setzt `padding-right: 24` auf `.MuiSelect-select`
 *  mit **drei** Klassen-Token (im ausgelieferten CSSOM gemessen — die Jest-Probe
 *  „W-3 (Kopf)" kalibriert genau diese Regel; ⚠️ die daneben zitierte Fundstelle
 *  `NativeSelectInput.js:69-72` ist in diesem Baum **nicht nachlesbar**, weil
 *  `node_modules` ein Symlink aus dem Arbeitsverzeichnis heraus ist. Der
 *  Mechanismus ist gemessen, der Dateiname nicht). Die `sx`-Regel der Komponente
 *  trug `'&& .MuiSelect-select'`, also ebenfalls drei Klassen-Token, und MUI's
 *  Regel kommt spaeter. Gemessen im Browser: das `padding-right` war **24 px**,
 *  der `pr: 2.5` (20 px) der Komponente griff NIE.
 *
 *  🔴 **Die Arithmetik, nachgerechnet — und hier standen drei falsche Ziffern.**
 *  `theme.js:361` setzt `html { font-size: 14px }`, also ist `1rem` = 14 px und
 *  `calc(1.5rem + 8px)` = **29 px** — genau der Wert, den die Orchestrator-Messung
 *  als `padding-right` gelesen hat. Die Luecke ist
 *  `padding-right − right − Pfeilbreite`; mit dem ALTEN `padding-right` 24 px und
 *  dem Pfeil (21 px = 1.5rem) ergibt sie `24 − 4 − 21 = **−1 px**` — genau der
 *  Befund, und **nicht** die „0", die hier stand (die kam aus einem Pfeil von
 *  20 px, den es nicht gibt).
 *
 *  🔴 **Deshalb `calc` und nicht eine nackte Zahl.** Die Luecke ist die
 *  DIFFERENZ zweier Groessen, von denen eine nicht hier steht: die Breite des
 *  Pfeils ist ein `SvgIcon` (`width: 1em`, MUI-Vorgabe `fontSize: 1.5rem` =
 *  21 px). Der Token setzt 21 px Pfeil + 8 px Luft. Die Luecke ist damit
 *  `8 − right` — **kein Band**: im Kopf (`right` 4) genau **4 px**, im
 *  Mobil-Panel (`right` 0) **8 px**. Hier stand „zwischen 4 und 12 px", und das
 *  verdeckte genau das, was zaehlt: der Kopf sitzt **exakt** auf der geforderten
 *  Untergrenze „≥ 4", ohne Reserve nach unten.
 *
 *  🔴 **Und der Satz zur Zukunft ist korrigiert — er war die halbe Wahrheit.**
 *  Hier stand: *„Eine spaetere MUI-Fassung mit groesserem Pfeil verkleinert die
 *  Luecke, statt sie stillschweigend aufzuheben: `calc` haelt die Luft als
 *  eigenen Summanden."* Der zweite Halbsatz stimmt, der erste war irrefuehrend:
 *  der **Icon-Term ist eine feste `1.5rem`**, die MUI's heutigen Wert nur
 *  zufaellig trifft. Waechst der Pfeil auf `1.75rem`, sinkt die Luecke im Kopf
 *  auf `8 − 3,5 − 4 = 0,5 px` — und `calc` verhindert das nicht. Was es jetzt
 *  verhindert, dass es **stumm** bleibt: `bereich.spec.ts` **B.9** misst den
 *  Abstand zwischen Etikett und Pfeil am gerenderten Knoten (≥ 4 px). Eine
 *  kuenftige MUI-Fassung macht die Probe rot, statt die Luecke stillschweigend
 *  aufzubrauchen.
 *
 *  ⚠️ Der Wert gilt fuer den KOPF und das MOBIL-Panel — die Deklaration haengt
 *  nicht an `kompakt`.
 *
 *  @useWhen
 *    - der Abstand zwischen Etikett und Pfeil eines MUI-`Select` soll stehen
 *  @dontUseFor
 *    - die Trefferflaeche → `SUCHBEREICH_MIN_HEIGHT_SX`
 *
 *  → `sx={{ '&&& .MuiSelect-select': { ...SUCHBEREICH_PFEILRAUM_SX } }}` */
export const SUCHBEREICH_PFEILRAUM_SX = {
  paddingRight: 'calc(1.5rem + 8px)',
};

/** Die HUELLE um einen Etikett-Chip — die Flaeche, die eine **Handlung** traegt,
 *  waehrend der Chip selbst ein reines Etikett bleibt.
 *
 *  K-B (UI-Durchgang 2, Teil 6, 2026-10-10): sie stand zweimal zeichengleich
 *  lokal in `ProductDetail.jsx` und `ActivityEntryRow.jsx` und ein drittes Mal,
 *  anders, am `EmailChipField`. Drei Abschriften einer Absicht sind zwei zu
 *  viel; die gemeinsame Fassung steht hier, die drei Stellen importieren sie.
 *
 *  Warum ueberhaupt eine Huelle: `MuiChip-clickable` und `MuiIconButton` tragen
 *  aus dem Theme `min-height: 44` unter grobem Zeiger (`theme.js`
 *  `MuiChip-clickable` bzw. `TOUCH_TARGET_COARSE_QUADRAT_ACTIVE`). Ein sichtbar
 *  18/24 px kleiner Chip wuerde damit auf einem Tablet sichtbar auf 44 px
 *  gehoben. Der Chip bleibt deshalb Etikett (kein `clickable`, kein `onClick`),
 *  und den Boden traegt allein die Huelle.
 *
 *  🔴 `minWidth` 44 gilt auf JEDEM Zeiger, die 44 px **Hoehe** nur unter grobem
 *  (`TOUCH_TARGET_COARSE`): eine Huelle mit `minHeight: 44` auf feinem Zeiger
 *  liesse die Zeile wandern, in der sie sitzt.
 *
 *  @useWhen
 *    - ein Chip/Etikett, das eine Handlung traegt (Link, Entfernen-Knopf), ohne
 *      selbst klickbar zu sein
 *    - als Link → `component="a"`/`component={RouterLink}` auf derselben `Box`
 *  @dontUseFor
 *    - ein Chip, der selbst klicken soll → `clickable` traegt den Theme-Boden,
 *      dann braucht es keine Huelle (aber auch keine sichtbare Kleinheit)
 *    - eine reine Layout-Zeile ohne Handlung
 *
 *  → `<Box data-testid="ui2-chip-wrapper" sx={{ ...CHIP_HUELLE_SX }}>`
 *
 *  ⚠️ `textDecoration`/`color` sind fuer die Anker-Faelle da; an einer `div`
 *  (dem Entfernen-Knopf des `EmailChipField`) sind sie wirkungslos, und das ist
 *  gewollt: EIN Token, drei Stellen. */
export const CHIP_HUELLE_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 44,
  ...TOUCH_TARGET_COARSE,
  textDecoration: 'none',
  color: 'inherit',
};

/** Die RUHEHOEHE der Trefferflaechen-Huelle am Etikett-Chip: **18 px**.
 *
 *  Der Bezugswert von `chipHuelleVertikalSx`: eine Huelle, die beim Aufdecken
 *  auf 24 bzw. 44 px waechst, gleicht den Zuwachs gegen DIESE Hoehe aus, damit
 *  die Zeile, in der sie sitzt, nicht wandert.
 *
 *  Nachbesserung 5 (UI-Durchgang 2, Teil 6, 2026-10-10): der Wert stand bis
 *  dahin roh im `sx` des `EmailChipField` (`height: offen ? 24 : 18`).
 *
 *  @useWhen
 *    - als Bezug fuer `chipHuelleVertikalSx`
 *  @dontUseFor
 *    - als Layout-Hoehe eines Etiketts — das ist die Ruhehoehe der HUELLE
 *
 *  → `chipHuelleVertikalSx(44)` */
export const CHIP_HUELLE_RUHE_HOEHE = 18;

/** Der vertikale AUSGLEICH einer Trefferflaechen-Huelle, die beim Aufdecken
 *  waechst.
 *
 *  Die Huelle traegt ihre volle Hoehe (`height`) in BEIDEN Zustaenden und nimmt
 *  den Zuwachs als negativen vertikalen Rand (`my`) wieder zurueck. Die
 *  Zeilenbox bleibt damit die Ruhehoehe, fuer JEDE uebergebene Hoehe:
 *
 *      height + 2 * my === CHIP_HUELLE_RUHE_HOEHE
 *
 *  Nachbesserung 5 (2026-10-10): ohne den Ausgleich wuchs die Zeile mit, in der
 *  die Huelle sitzt — das Empfaengerfeld des Composer sprang beim Aufdecken des
 *  Entfernen-× gemessen um **26 px** (grober Zeiger) bzw. **6 px** (feiner
 *  Zeiger). Das ist ein Layoutsprung, kein Schoenheitsfehler (R16 W1, Runde 1
 *  und WV1).
 *
 *  🔴 Der Rand ist ein **px-String**, keine nackte Zahl: `sx` rechnet eine Zahl
 *  ueber `theme.spacing` (Faktor 8), `my: -3` waere also **−24 px** und risse
 *  ein noch groesseres Loch. Gemessen am 2026-10-10 ueber
 *  `unstable_styleFunctionSx`: `my: '-3px'` → `marginTop: -3px`,
 *  `my: -3` → `marginTop: -24px`.
 *
 *  🔴 Der UEBERSTAND ist die Kehrseite desselben Ausgleichs und bewusst
 *  hingenommen (Nachbesserung 6, 2026-10-10). Weil die Huelle ihre VOLLE Hoehe
 *  traegt, ragt sie ueber den Chip hinaus: der Chip ist ~21 px hoch (18 px
 *  Labelzeile + 2 x `py: 0.2`), die Huelle auf grobem Zeiger 44 px und
 *  zentriert → **~11 px je Seite**. Der Zeilenabstand ist `gap: 0.5` = **4 px**,
 *  in die naechste Zeile dringen damit **~7 px** ein. Auf feinem Zeiger sind es
 *  1,4 px (< 4 px) — **kein** Eindringen.
 *
 *  🔴 Was er NICHT trifft, und das ist die Bedingung seiner Hinnahme: wo ein
 *  Nachbar liegt, deckt ihn der SPAETERE Chip zu. Jeder Chip traegt einen
 *  eigenen Stapelkontext (`EmailChipField.jsx`, `isolation: 'isolate'`), also
 *  malt er in DOM-Reihenfolge ueber dem Ueberstand des frueheren — die
 *  Trefferflaeche des × ist dort so hoch, wie der Nachbar sie laesst, nicht 44.
 *
 *  🔴 MIT ZAHL (Nachbesserung 7, 2026-10-10), gemessen bei 390 px, grobem
 *  Zeiger und vier Empfaengern in einem 1-px-Raster ueber den SCHNITT
 *  Huelle ∩ Nachbarchip (`tests/design/ui2-k-b-ausreisser.spec.ts`, K9.3):
 *
 *    · INNERHALB der Pillenform des Nachbarn (dort ist er sichtbar) treffen
 *      **0 von 56** Punkten den frueheren Chip — mit Stapelkontext.
 *      Ohne den Stapelkontext sind es **3 von 56**: das ist die Groesse des
 *      Befunds, den Nachbesserung 6 behebt, und der Rotbeweis in K9.3.
 *    · In der ABGERUNDETEN ECKE — ausserhalb der Silhouette, wo kein Chip
 *      sichtbar ist — bleiben **≈ 11 Punkte** Flaeche des ×; dort trifft ein
 *      Tipp auf das rechte Ende des Nachbarn weiterhin das × des frueheren.
 *      Hingenommen und nur gezaehlt, nicht zugesichert: eine Zusicherung
 *      darauf waere eine auf eine Geometrie, die niemand beheben kann.
 *
 *  ⚠️ Die frueher hier genannte Zahl („218 Punkte") zaehlte ueber den ganzen
 *  Ueberstand inklusive der freien Flaeche NEBEN dem Nachbarn, nicht ueber den
 *  Schnitt — sie beschrieb nicht, was der Fall zusichert.
 *
 *  @useWhen
 *    - eine Trefferflaechen-Huelle, die im Ruhezustand zusammengeklappt ist und
 *      beim Aufdecken auf 24/44 px waechst (`EmailChipField`, K9)
 *  @dontUseFor
 *    - eine Huelle, deren Boden IMMER anliegt → `CHIP_HUELLE_SX` genuegt
 *      (`ProductDetail.jsx`, `ActivityEntryRow.jsx`)
 *    - eine horizontale Groesse — der Ausgleich ist vertikal
 *
 *  → `sx={{ ...chipHuelleVertikalSx(44) }}` */
export function chipHuelleVertikalSx(hoehe) {
  return {
    height: hoehe,
    my: `${-(hoehe - CHIP_HUELLE_RUHE_HOEHE) / 2}px`,
  };
}


// ── Widths (BW1 / G0b.1) ───────────────────────────────────────
// Die eine Quelle der Breiten. Angelegt in G0b.1 (Plan
// `docs/superpowers/plans/2026-10-11-ui2-t10-g0-bausteine.md` §3.1/§3.2) aus
// dem Konzept `docs/forge-ux-redesign/concepts/inhaltsbreite.md`; jede Zeile
// nennt ihre Konzeptzeile.
//
// 🔴 Noch KEINE Verbraucher (Plan §3.1, G0b.1): G0b.2 (`PageContainer`,
// `ViewHeader`), G0b.3 (`useContainerWidth`, `useColumnPriority`) und G0b.4
// (`TableViewport`, `detailRailSx`, `PROSE_SX`) lesen sie erst. Bis dahin ist
// dieser Block rein additiv und aendert nichts Gerendertes.
//
// 🔴 `RAIL_WIDTH` (oben, die 3-px-Status-Schiene) ist NICHT gemeint und bleibt
// unberuehrt — Konzept §0.2:71, Grill B1. Der neue Token heisst
// `DETAIL_RAIL_WIDTH`.
//
// 🔴 Kein Einfrieren: das Haus friert seine Token nicht ein — dieses Modul
// enthaelt keinen Einfrier-Aufruf. Die Nachbarn sind einfache Objekte, diese
// hier sind es auch.

/** `CONTENT_WIDTH` — die Deckel der drei Breitenmuster (Konzept §1.1:154-156) und drei Baustein-Deckel.
 *
 *  - `reading` = **1280** — der Wert, den `PageContainer` heute als Rueckfall
 *    traegt (`PageContainer.jsx:124`, `?? 1280`), und gegen den die 54 Seiten
 *    entworfen sind (Konzept §1.2:162-167). Er wird **benannt, nicht geaendert**.
 *  - `wide` = **1600** = 1280 + 320 — genau eine Rail mehr (Konzept §1.2:169-175).
 *  - `full` = **kein fester Deckel** (Konzept §1.1:156, OE-W3 §7:944) — das
 *    Spaltenbudget begrenzt (§4). `null` ist die ehrliche Form: MUI liest es als
 *    „kein `maxWidth`" — **gepinnt, nicht angenommen**
 *    (`contentWidthFull.test.jsx` rendert `sx={{ maxWidth: CONTENT_WIDTH.full }}`
 *    und liest `max-width` als leeren String; Kalibrierung `reading` → `1280px`).
 *    Ein Zahlenwert waere eine Erfindung. 🔴 **G0b.2 (2026-10-12) hat den Prop-Pfad
 *    `<Container maxWidth={…}>` gar nicht erst genommen** — der Deckel steht als
 *    skalarer `sx`-Wert auf der inneren Box beider Bausteine, damit ist `full` an
 *    `PageContainer` UND `ViewHeader` gerendert gepinnt (`…breite.test.jsx`).
 *  - `prose` = **640** und `proseSm` = **560** — die Lesebreiten des Konzepts
 *    (§1.5:216-217). 🔴 Das Konzept setzt **`body1` = 16 px** voraus
 *    (`inhaltsbreite.md:216`); **das Haus setzt `body1` = 14 px** (`theme.js:361`
 *    `html { font-size: 14px }`, Messtabelle `design-tokens.md:146-150`). Auf
 *    640 px stehen bei 14 px daher rund **75-86 Zeichen** je Zeile — die 66-75 des
 *    Konzepts mit 16/14 skaliert (**Schaetzung**, keine Messung) —, also **ueber**
 *    dem normativen Korridor 66-75 (§1.5:219-222). Der Wert bleibt (Konzept); welcher
 *    Fall fuer `PROSE_SX` gilt, entscheidet G0b.4 (Plan §3.2:584) — Akte
 *    `docs/backlog-auto-repair/2026-10-12-lesebreite-prose-gegen-16px-hergeleitet.md`.
 *  - `panel` = **520** — die Einzelmeldung (Fehlerseite) ohne Schale
 *    (Konzept §2.1 WF6:263, §5.1:667).
 *
 *  @useWhen
 *    - ein Seiten- oder Baustein-Deckel gebraucht wird (`maxWidth`, `PROSE_SX`)
 *  @dontUseFor
 *    - die 3-px-Status-Schiene — das ist `RAIL_WIDTH`, nicht `CONTENT_WIDTH` */
export const CONTENT_WIDTH = {
  reading: 1280,   // §1.2:162 — das Lesemuster; 🔴 der RUECKFALL ist mit G0b.5 (2026-10-13) entfallen: Seite UND Kopf lesen `CONTENT_WIDTH[width]`
  wide: 1600,      // §1.2:169-175 — 1280 + 320
  full: null,      // §1.1:156 / OE-W3 §7:944 — kein fester Deckel
  prose: 640,      // §1.5:216 — Lesebreite (Konzept: body1 = 16 px; Haus: 14 px — s. Kopf)
  proseSm: 560,    // §1.5:217 — die 14-px-Lesebreite des Konzepts (640 × 14/16)
  panel: 520,      // §2.1 WF6:263 / §5.1:667 — Einzelmeldung ohne Schale
};

/** `DETAIL_RAIL_WIDTH` — die Breite der Detail-Rail (Konzept §0.1:50, §2.3:335).
 *
 *  - `narrow` = **320** — der heutige Wert. 🔴 Er **stand bis G0b.5
 *    (2026-10-13) dreimal als Literal** im Baum (PO-Detail, Fall-Detail,
 *    K+N-Sendung; Konzept §0.2:60) und ist seitdem an allen drei Stellen ein
 *    Token-Aufrufer — der Satz „steht heute dreimal als Literal" war nach
 *    diesem Schnitt falsch.
 *  - `wide` = **640** — die Rail auf einer `wide`-Seite (OE-W1 = B):
 *    `rail = clamp(320, Inhaltsbreite − 960, 640)`, die Hauptspalte bleibt bei
 *    **936** (Konzept §2.3:335; „Rail 640" §1.1:155).
 *
 *  ⚠️ Das Konzept nennt namentlich nur `DETAIL_RAIL_WIDTH.narrow` (§2.3:335).
 *  Der Schluessel `wide` ist hier gesetzt — parallel zu `CONTENT_WIDTH.wide` —,
 *  weil der Wert 640 im Konzept feststeht und einen Namen brauchen muss.
 *  Erfunden ist die **Zahl** nicht, nur ihr Name. */
export const DETAIL_RAIL_WIDTH = {
  narrow: 320,   // §2.3:335 — der heutige Wert
  wide: 640,     // §2.3:335 / §1.1:155 — die Rail der wide-Seite
};

/** `COLUMN_PRIORITY` — die Schwellen der Spalten-Prioritaet, gemessen in
 *  **Container**-Breite (nicht Viewport; Konzept §3.1, NW3.1:548), Konzept §3.2.2:442-448.
 *
 *  🔴 **Startwerte, gesetzt — nicht gemessen** (Grill W9, Konzept §3.2.2:439-440
 *  und NW3.8:555). G0b misst die Zellbreiten je Spalte am gerenderten Bestand
 *  nach; **weicht die Messung ab, folgt die Zahl der Messung**, nicht dieser Block.
 *
 *  - `kern` = **0** — immer sichtbar, auch im Rollfall (§3.2.2:444).
 *  - `lg` = **1024** · `xl` = **1360** · `xxl` = **1728** (§3.2.2:445-447).
 *  - `frei` = **nie automatisch** (§3.2.2:448) — nur ueber die Nutzerauswahl
 *    (OE-W5, §3.5:531-533).
 *
 *  🔴 **Drei Leser, ZWEI Bedeutungen — und sie sind nicht dieselbe** (Grill W3,
 *  2026-10-14). Fuer den **Hauptverbraucher** `useColumnPriority.waehleSichtbare`
 *  ist der Block seit der G1b-Nachbesserung 1 ein **RANG**: die Zahl ordnet die
 *  Spalten, entschieden wird per **Fit** gegen `COLUMN_MAX` (Konzept §3.2.2,
 *  Block „G1b-Nachbesserung 1" — Orchestrator-Entscheid 2026-10-14). Fuer
 *  `ViewHeader.jsx:48-50` und `useContainerWidth.NARROW_BELOW` ist er dagegen
 *  weiter eine **Schwelle** (`containerWidth >= COLUMN_PRIORITY[stufe]`). Der
 *  Satz darunter gilt deshalb fuer die **Schwellen-Leser**, nicht fuer den Fit —
 *  wer den Block umbaut, muss beide Aufrufer sehen, sonst bricht der eine still,
 *  waehrend der andere gruen bleibt.
 *
 *  🔴 `frei` ist `Infinity`, **nicht `null`** — und das ist keine Kosmetik: die
 *  Schwellen-Leser vergleichen `containerWidth >= COLUMN_PRIORITY[stufe]`, und
 *  `x >= null` ist `x >= 0`, also **wahr**. Mit `null` erschiene eine
 *  `frei`-Spalte bei jeder Breite, also genau das Gegenteil der Regel.
 *  `Infinity` macht den Vergleich immer falsch.
 *
 *  Der Vergleichsoperator `>=` ist aus dem Konzept **abgeleitet** („erscheint
 *  **ab** dieser Container-Breite", §3.2.2:442) — ausgeschrieben ist er dort
 *  nicht; er gehoert zur **Schwellen**-Lesart. ⚠️ `COLUMN_PRIORITY` darf
 *  deshalb **nie** durch `JSON` laufen: `JSON.stringify(Infinity)` ergibt `null`,
 *  und `frei` kippte damit auf die falsche Semantik.
 *
 *  🔴 `kern` und `frei` sind die einzigen deutschen Schluessel **dieses Blocks**
 *  (`FOCUS_RING` traegt ebenfalls deutsche); sie folgen woertlich den Stufennamen des
 *  Konzepts (§3.2.2:442-448) — Rueckverfolgung vor Sprachgrenze.
 *
 *  ⚠️ Diese Stufen messen die **Container**-Breite. Die RMA-Fallliste staffelt
 *  heute nach **Viewport** („bei 1024-1199 faellt `EXTERNAL`", `cases.md:445`,
 *  zitiert in `RMACasesDashboardBody.jsx` › `isMobile`-Verzweigung); beide Zahlen sind 1024 und
 *  meinen Verschiedenes. Aufgeloest wird das beim Umbau der Fallliste (die
 *  Ansichtskarte G1-G15, Plan §8:870), nicht in G0b. */
export const COLUMN_PRIORITY = {
  kern: 0,          // §3.2.2:444 — immer (auch im Rollfall)
  lg: 1024,         // §3.2.2:445
  xl: 1360,         // §3.2.2:446
  xxl: 1728,        // §3.2.2:447
  frei: Infinity,   // §3.2.2:448 — nie automatisch, nur per Nutzerauswahl
};

/** `COLUMN_MAX` — Ober- und Untergrenze je Spaltenart (Konzept §3.3:492-498).
 *
 *  `min`/`max` kommen **ausschliesslich** von hier: der Descriptor einer Spalte
 *  traegt `{ key, priority, kind }`, und der Baustein leitet
 *  `min/max = COLUMN_MAX[spalte.kind]` ab — **kein eigenes `max` am Descriptor**
 *  (Grill W12, Konzept §3.3:500-504, NW3.3:550). Zwei Quellen fuer dieselbe Zahl
 *  sind genau der Zustand, den das Konzept abschafft.
 *
 *  Zweck: ohne Obergrenze wird aus einer 6-Spalten-Tabelle bei 3440 px ein Gitter
 *  aus sechs 500-px-Spalten (§3.3:488-490). Der Ueberschuss geht in neue
 *  **Spalten**, nicht in breitere Zellen (§3.3:506-507, NW3.4:551).
 *
 *  Alle fuenf Arten sind im Konzept beziffert (§3.3:494-498) — hier steht keine
 *  erfundene Zahl und keine fehlende Art. */
export const COLUMN_MAX = {
  identity: { min: 200, max: 360 },   // §3.3:494 — Kennung/Titel
  text:     { min: 120, max: 320 },   // §3.3:495 — Lieferant, Kunde, Kategorie
  number:   { min:  72, max: 160 },   // §3.3:496 — Menge, Betrag, Prozentsatz
  date:     { min:  96, max: 160 },   // §3.3:497 — 31.07.2026
  chip:     { min:  88, max: 200 },   // §3.3:498 — Paid, Order placed
};

/** `TABELLE_ZEILENHOEHE_PX` — die **ganzzahlige Zeilenhöhe** einer Tabelle mit
 *  klebender erster Spalte (G2b-Nachbesserungen 6 und 7, 2026-10-18).
 *
 *  🔴 **Warum es diesen Token gibt.** Unter einer GEBROCHENEN Zeilengrenze fiel
 *  die Zeilenlinie in der klebenden Spalte aus (Pixelprobe PW6e, `/overview`
 *  bei 940 px): die Nachbarzelle trug sie, die klebende nicht. Die Grenze lag
 *  bei `48.156` px — eine Summe aus zwei gebrochenen Textzeilen
 *  (`body2` 17.515625 + `caption` 13.640625), 16 px Polster und 1 px Rahmen.
 *  Mit GANZZAHLIGER Zeilenhöhe verschwindet der Ausfall in allen sechs
 *  gemessenen Konfigurationen (dpr 1/2, Höhen 900/1000/1037).
 *
 *  ⚠️ **Die Ursache ist eine gemessene KORRELATION, kein bewiesener
 *  Mechanismus:** gebrochene Zeilengeometrie ↔ fehlende Linie. Die
 *  naheliegende Erklärung — die klebende Zelle rastet als eigene Ebene ein und
 *  ihr deckender Grund überdeckt die Linie der Folgezeile — steht als Hypothese
 *  an `TableViewport.stickySx`; belegt ist sie nicht.
 *
 *  🔴 **Warum 16 und nicht 20 (Nachbesserung 7, 2026-10-18).** 16 ist die
 *  Zeilenhöhe, die die DICHTE des heutigen Standes hält: der gemessene Ist-Zustand
 *  (`/overview` 940×1000, dpr 1/2) hatte Zeilen von 48.156 px; mit 16 px werden
 *  daraus **49 px** (sieben Zeilen, vorher 48.156). Mit **20** wurden dieselben
 *  Zeilen **57 px** — 9 px höher je Zeile (+18 % Höhe), sichtbar lockerer als
 *  jede andere Liste. 18 px lag bei 53 px, also ebenfalls über dem Ist-Stand.
 *  Der Wert ist damit **gemessen**, nicht abgeleitet.
 *
 *  ⚠️ **20 war die erste Fassung und ist verworfen.** Sie war als „über allen
 *  drei Textzeilen des Hauses" begründet (body1 21 → 20, body2 17.52 → 20,
 *  caption 17.43 → 20); die Begründung stimmt weiter, der PREIS war ungemessen.
 *  Die Messung zeigt ihn: 57 statt 49 px.
 *
 *  Verbraucher ist genau EIN Ort: `TableViewport.stickySx` (ohne
 *  `stickyFirstColumn` existiert die Regel nicht). Gilt für `td` und **alle
 *  Nachfahren** (`td *`) — nicht nur `Typography`: MUI setzt die Zeilenhöhe je
 *  Variante selbst (`body2` 1.43, `caption` 1.66, die Frist-Caption auf
 *  `/overview` sogar 1.3), eine Regel nur auf `td` liesse die gemessenen Zeilen
 *  gebrochen. Bausteine mit EIGENER fester `height` bleiben davon unberührt,
 *  weil `line-height` eine feste Höhe nicht überschreibt: MUI-Chip `small`
 *  (24 px, Theme-Vorgabe), der Bundle-Chip in `ProductsTable` (20 px im `sx`),
 *  `ProductThumb` (28 px über `size`), `IconButton` `small` (SVG 17.5 + 2 × 5
 *  px Polster = 27.5 — das SVG holt `TABELLE_ICON_PX`, s. dort).
 *
 *  🔴 **Die frühere Begründung „Pill-Fall" war falsch (Nachbesserung 8,
 *  2026-10-18).** Hier stand, die 44.5-px-Zeile (Zeilen 17/18) käme von einer
 *  `Pill` ohne `MuiTypography-root`-Klasse. Die Browser-Messung `g2bzm` an
 *  genau dieser Zeile zeigt das Gegenteil: ihr Status ist ein MUI-`Chip`
 *  `small` (feste 24 px), und die höchste Zelle ist der ⋮-Knopf
 *  (`RowActionsMenu`, `IconButton size="small"`) mit **27.5 px** aus
 *  17.5-px-SVG + 2 × 5 px. `line-height` kann ihn nicht erreichen — ein SVG
 *  hat keine Zeilenhöhe. Den Träger setzt `TABELLE_ICON_PX`. */
export const TABELLE_ZEILENHOEHE_PX = 16;

/** `TABELLE_ICON_PX` — die **ganzzahlige Größe der SVG-Icons in den Rumpfzellen**
 *  einer Tabelle mit klebender erster Spalte (G2b-Nachbesserung 8, 2026-10-18).
 *
 *  🔴 **Warum es diesen Token gibt.** `TABELLE_ZEILENHOEHE_PX` macht Text
 *  ganzzahlig, aber nicht Icons: ein MUI-`SvgIcon` mit `fontSize="small"` ist
 *  `1.25 rem` × `htmlFontSize` 14 = **17.5 px** breit und hoch (es skaliert über
 *  `1em`). Im ⋮-Knopf (`RowActionsMenu`, `IconButton size="small"`) ergibt das
 *  17.5 + 2 × 5 px Polster = **27.5** und mit 16 px Zellpolster + 1 px Rahmen
 *  eine Zeile von **44.5 px** — gebrochen.
 *
 *  🔴 **Gemessen** (Browser-Probe `g2bzm`, `/overview` 940×1000, dpr 1, Zeile 18
 *  in 1-basierter Zählung): die Zellnachfahren dieser Zeile sind
 *  `a` (16 px), `span.MuiTypography-body2` (16), Text (16), MUI-`Chip` `small`
 *  (24) und der ⋮-`button.MuiIconButton-sizeSmall` (**27.5**, darin
 *  `svg.MuiSvgIcon-fontSizeSmall` **17.5**). Der Knopf ist der Träger; die
 *  Zeile ist nur dort gebrochen, wo die Expected-Spalte keine Frist-Caption
 *  trägt (dann ist er das höchste Element).
 *
 *  **18 → 28 → 45.** 18 + 2 × 5 = 28, die Zeile 28 + 16 + 1 = **45**, ganzzahlig.
 *  18 ist die kleinste ganze Zahl über dem Ist-Stand 17.5; 20 wäre unnötig
 *  größer als das Haus-Symbol.
 *
 *  **Geltungsbereich:** `TableViewport.stickySx`, `td`-Nachfahren (`td svg`) —
 *  nur mit `stickyFirstColumn`. Icons im KOPF (`th`, z. B. Sortier- oder
 *  Erklär-Symbole) bleiben unberührt: sie sind nicht Teil der Rumpfzeile.
 *  Auf `/overview` trägt genau der ⋮ ein Icon in der Zelle; auf `/products`
 *  keines (die `Price reco`-`Pill` ist Text, `ProductThumb` ein Bild). */
export const TABELLE_ICON_PX = 18;

/** `TABELLE_KNOPF_PX` — die **ganzzahlige Höhe eines Text-Knopfes in einer
 *  Datenzelle** einer Tabelle mit klebender erster Spalte (G2b-Nachbesserung 13,
 *  2026-10-18; Wert am 2026-10-19 auf die Messung gezogen).
 *
 *  🔴 **Warum es diesen Token gibt.** `TABELLE_ZEILENHOEHE_PX` (16) gilt auch
 *  für die Nachfahren einer Datenzelle (`td:not([colspan]) *`) — und damit für
 *  die Textzeile im MUI-`Button`. Mit 16 px Zeilenhöhe bleiben
 *  `16 + 2 × 3 Polster + 2 × 1 Rahmen = 24 px`; der Knopf schrumpft also in
 *  der Datenzelle, obwohl seine Beschriftung dieselbe ist. Gemessene Flächen
 *  (NB12): `SecondLifeBestand.jsx` („Edit") und `secondLife/OpenCases.jsx`
 *  („Create"/„Release"), beide `variant="outlined"`.
 *
 *  🔴 **28 = nächster ganzer Wert zum GEMESSENEN Ist-Stand 27,91 px**
 *  (Browser-Messung des Orchestrators, `/second-life` mit Bestand-Stub, dpr 1,
 *  alle vier Knöpfe). Die Rechnung am Kode, nicht abgeleitet: `theme.js:368`
 *  setzt `html { font-size: 14px }`, deshalb ist MUI v7s `size="small"`
 *  (`fontSize: 0.8125rem`) **11,375 px** groß; `line-height: 1.75` ergibt
 *  19,90625 px, dazu `padding: 3px 9px` (2 × 3) und der 1-px-Rahmen
 *  (`variant="outlined"`, 2 × 1) = **27,90625 px ≈ 27,91**.
 *
 *  ⚠️ **Die frühere 32,75-Rechnung war MUI's STANDARD, nicht diese Fläche.**
 *  Sie nimmt 13 px (16-px-Wurzel) und 4-px-Polster an — beides gilt hier nicht:
 *  die Haus-Wurzel ist 14 px, MUI v7s `sizeSmall` trägt 3 px Polster. Der Knopf
 *  war nie 32,75 hoch; 32 wäre 4,09 px GRÖSSER als der gemessene Ist-Stand
 *  gewesen. 28 liegt 0,09 px darüber und hält die Zeile ganzzahlig.
 *
 *  **Rechnung der Zeilen (28):** Aktionszeile `28 + 16 + 1 = 45`; mit EINER
 *  Sperrgrund-Caption `28 + 4 + 16 + 16 + 1 = 65`; die Aktionszelle mit
 *  Grund + Hinweis + Knopf `28 + 4 + 16 + 4 + 16 + 16 + 1 = 85` (je gerechnet,
 *  nicht gemessen; der 4-px-Abstand ist `Stack spacing={SP.chip}`).
 *
 *  **Geltungsbereich:** `TableViewport.stickySx`, `td:not([colspan]) .MuiButton-root`
 *  — nur mit `stickyFirstColumn`, nur DATENzellen. Der `colspan`-Knopf der
 *  Leer-/Fehlerfläche behält seine Höhe (NB9), und ein `IconButton` ist nicht
 *  betroffen (kein `.MuiButton-root`; seinen Träger setzt `TABELLE_ICON_PX`).
 *
 *  🔴 **Regel 22 Nr. 8 + §4.1a:** das Theme nimmt den Grobzeiger-Boden in
 *  Tabellenzellen ab `sm` ausdrücklich zurück (`theme.js:702-705`,
 *  `TOUCH_TARGET_COARSE_OFF` → `auto`); die Regel hält den Knopf dort auf 28
 *  und setzt unter `sm` dieselben 44 wie das Theme (`down('sm')`). Sie spreadet
 *  `TOUCH_TARGET_COARSE` NICHT — das setzte 44 dort, wo das Theme ihn gerade
 *  zurückgenommen hat. */
export const TABELLE_KNOPF_PX = 28;

/** `SEARCH_MAX` — der Deckel des Suchfelds in Z1 auf einer `full`-Seite
 *  (Konzept §4.2:596, NW4.2:648).
 *
 *  Auf `full` fuellt die Suche nur bis hierher; der Rest von Z1 bleibt leer, und
 *  Zaehler/Seitenaktionen bleiben am rechten Rand. Auf `reading`/`wide` bleibt die
 *  Regel **N2.9** des Tabellenkonzepts woertlich („die Suche fuellt den Rest") —
 *  dort wirkt `SEARCH_MAX` **nicht**. Wer N2.9 woertlich auf `full` anwendet, baut
 *  ein 3000-px-Suchfeld (§4.2:601-604).
 *
 *  ⚠️ Das Konzept schreibt **„≈ 480 px"** (§4.2:596, NW4.2:648) — der Wert ist
 *  benannt und als Naeherung gekennzeichnet. **480** ist die Umsetzung.
 *
 *  ⚠️ Offene Spannung: die Tabellenprobe **P24** verlangt „Suchbreite ≥ 40 % der
 *  Leiste" **unbedingt** (`tabellen-filter-suche.md:1489`), N2.9 nimmt `full`
 *  aber aus. Auf `full` bei 2560 (Container ≈ 2184) waeren 480 px ≈ 22 %. Welche
 *  Regel dort gilt, entscheidet **G0b.4** (das `searchMax` verdrahtet, Plan §3.2:584). */
export const SEARCH_MAX = 480;   // §4.2:596 / NW4.2:648

/** `SUCHE_MIN_BREITE_KOMPAKT` — der **Boden des Suchfelds** in der KOMPAKTEN Z1
 *  am Tablet (R15 W4, Nachbesserung 4, 2026-10-18).
 *
 *  Die Zahl stand bis dahin an vier Stellen ohne gemeinsame Quelle:
 *  `ListToolbar` (`minWidth`), die Playwright-Probe PW6c, das Changelog und die
 *  Feature-Doc. Sie ist eine Design-Konstante, kein Messwert: bei 768 px
 *  drueckten „Filters", ⟳ und ⋮ das Feld ohne Boden auf **~0 px** (Fotobefund).
 *  160 px ist der Boden, ab dem das Feld lesbar und tippbar bleibt — die
 *  gemessene Breite liegt mit ~219–234 px deutlich darueber.
 *
 *  ⚠️ Am Telefon gilt sie **nicht**: dort ist die Suche die schmale Pille
 *  (`SearchTrigger`-Mobilzweig) und klappt beim Fokus auf. */
export const SUCHE_MIN_BREITE_KOMPAKT = 160;

/** `DICHTER_TEXT_LINK_SX` — der **Norm-Boden** eines TEXT-LINKS in einer dichten
 *  Zeile (G1c-2, Nachbesserung 5, 2026-10-16; R16 B1 der Runde 3).
 *
 *  🔴 **Die Norm, nicht die Haus-Regel.** Ein Textlink in einer Tabellenzelle ist
 *  ~16–18 px hoch und liegt damit unter der Untergrenze von **24 px**
 *  (WCAG 2.2 AA, 2.5.8 — `pattern-library.md` §Touch targets: *„Pflicht (Norm):
 *  ≥ 24 × 24 px"*). Die Haus-Regel **44** (N11.19, Regel 22 §8) gilt daneben und
 *  ist hier **nicht** erreichbar: unter der Oberkante des Links bleiben bis zur
 *  **Zeilenkante** nur **25 px** (gemessen 2026-10-16, M2g-Diagnose: `tr`
 *  endet 25 px unter der Link-Oberkante, Link 14 px hoch) — ein 44-px-Boden
 *  passt dort nicht hinein.
 *
 *  🔴 **Und er wuerde der Folgezeile NICHTS nehmen — das stand hier bis zum
 *  2026-10-16 als Begruendung und ist widerlegt (R15 W-1).** Mit einem 44er-Boden
 *  gemessen (Diagnose D44, `frontend/tests/listen/produkte.spec.ts` M2g): der
 *  Punkt `+25` unter der Link-Oberkante gehoert dem Link **trotzdem nicht**, und
 *  der Namens-Link der Folgezeile behaelt sein Hit-Testing. Was den Ueberstand
 *  deckt, ist die **Zeilenkante** (ab ihr malt die Zelle der Folgezeile darueber)
 *  — **nicht** die Band-Klemme: deren Padding-Box liegt bei ≈44 px
 *  (`BandInhaltUnten + 24`), also weit unterhalb der gemessenen Grenze.
 *  🔴 **Die Zahl stand hier bis zur Nachbesserung 8 (2026-10-16) als „≈41 px"
 *  und ist seither ≈44 — ABGELEITET, nicht gemessen (R15 W-4 der Runde 5).**
 *  Sie war aus der Inline-Box gerechnet (Zeilenbox ≈19,9 − δ≈3 + 24); die
 *  Nachbesserung 7 hat den Link **blockifiziert**, damit faellt δ auf ≈0 und die
 *  Unterkante liegt bei ≈19,9 + 24 ≈ **44**. Die Aussage bleibt (44 > 28 > 25,
 *  die Klemme deckt den Ueberstand nicht), die Ziffer nicht. Wer sie braucht,
 *  messt sie im Browser nach — sie ist hier nicht gemessen. Die Wahl
 *  24 gegen 44 traegt damit die **Norm** (24 ist die Untergrenze) und die
 *  Tatsache, dass 44 in dieser Zeile **keine groessere wirksame Flaeche** ergaebe
 *  — **nicht** „44 nimmt dem Nachbarn das Hit-Testing". Der Orchestrator hat die
 *  Norm statt der Auslegung gewaehlt: **24 px, auf JEDEM Zeiger** — die Norm ist
 *  zeigerunabhaengig, ein `@media (pointer: coarse)` waere an ihr vorbei.
 *
 *  🔴 **Die Flaeche ist 24 × 24 — auch in der BREITE (R16 N2, 2026-10-16).**
 *  `height` allein trug nur die HOEHE: `left: 0; right: 0` nimmt die Breite des
 *  Link-Kastens, und der ist bei einem INLINE-Link die **Textbreite** der Marke.
 *  Gemessen in prod (streng lesend, 2026-10-16, Messung des Orchestrators): die
 *  kuerzeste Marke hat **3 Zeichen** (`ROK`, `WPM`, `SSP`; 58 Produkte mit
 *  ≤ 3 Zeichen) — ihre Textbreite faellt unter 24 px, die Norm waere auf der
 *  Breitenachse verfehlt. Deshalb `minWidth` aus `TOUCH_TARGET_MIN`: die Flaeche
 *  ist damit **laengenunabhaengig** 24 px breit.
 *  🔴 **Am SPALTEN-Link ist das folgenlos** (`ProductsTable.jsx`, Brand-Spalte):
 *  sein Kasten ist als Block-Link die Zellbreite, `left/right` spannen bereits
 *  weiter als 24, `minWidth` greift dort nicht. Am **Band-2-Link** waechst die
 *  Flaeche nach LINKS — der `ProductsTable.jsx` verankert sie dort an der
 *  RECHTEN Kante (`MARKE_BAND_LINK_SX`: `left: 'auto'; right: 0`), weil die
 *  Marke am ENDE des Bandes steht und ein nach rechts wachsender Boden an
 *  dessen `overflow: hidden` geklippt wuerde.
 *  🔴 **Nachbesserung 9 (2026-10-16): der ANKER allein trug nicht — es gehoert
 *  `width: '100%'` dazu.** Ohne sie ist der Kasten SHRINK-TO-FIT (CSS 2.1
 *  §10.3.7), also genau 24 px AN der rechten Kante statt ueber die Linkbreite;
 *  bei einer Marke breiter als ~48 px faellt die Link-Mitte aus der Flaeche (im
 *  A/B gemessen: `M2g` rot). Kurzform: **volle Breite, rechts verankert,
 *  waechst bei kurzer Marke nach links.**
 *  🔴 **Hier stand bis zur
 *  Nachbesserung 8 (2026-10-16) „nach RECHTS in leeren Raum" — den leeren Raum
 *  gibt es nur, solange die Zeile nicht voll ist (R15 W-2 = R16 W1 der
 *  Runde 5).** Voll ist sie genau dann, wenn der Vorderteil hat schrumpfen
 *  muessen; dann liegt die Marke buendig rechts, und die wirksame Breite fiel
 *  auf die Textbreite zurueck — bei den 58 Produkten mit ≤ 3 Zeichen unter 24.
 *  🔴 **Die zwei Rueckfallspalten koennen laenger sein als jede Marke:**
 *  `shp_vendors.metaobject_name` **5–49** Zeichen, `vendor_name` **3–30** (prod,
 *  2026-10-16). **Genau deshalb traegt der Band-2-Link seit der Nachbesserung 8
 *  einen `title`** (den Markennamen, an beiden Zweigen) und die Marke die
 *  Kuerzungs-Hausform (`MARKE_BAND_KUERZUNG_SX`: `maxWidth` + Ellipse): ein
 *  Rueckfallname ueber der Bandbreite kuerzt sich **selbst** und ist im Tooltip
 *  vollstaendig lesbar. 🔴 **Bis dahin stand hier „der Band-2-Link traegt
 *  bewusst keinen `title` (R16 B2: genau EINE Sprechblase)" — das galt fuer den
 *  Fall „gekuerzt ODER verdeckt" und ist mit der Kuerzung der Marke ueberholt
 *  (R16 B1/W2 der Runde 5).** ⚠️ **Kein zweites Muster in derselben Zeile** (R15
 *  W-2 der Runde 6, 2026-10-16): hier stand, es staenden „zwei
 *  Sprechblasen-Muster nebeneinander" (nativ am Band, MUI-Popup „Open vendor
 *  page" an der Brand-Spalte). Das kann per Konstruktion nicht eintreten — das
 *  Band-Fragment haengt an `!sichtbar.has('brand')`, die Spalte samt Popup an
 *  `sichtbar.has('brand')`: Negation desselben Praedikats, je genau eine
 *  Aufrufstelle. In einer Zeile steht genau EIN Muster.
 *
 *  🔴 **Warum eine FLAECHE und kein `minHeight`.** `TOUCH_TARGET_MIN` setzt
 *  `minHeight: 24` — an einem Link waechst damit die Zeilenbox und mit ihr die
 *  Tabellenzeile. Der Boden waechst hier als absolut positionierter `::after`
 *  **nach aussen**, ohne am Layout zu ruecken (dieselbe Bauart wie
 *  `MARKE_KARTE_LINK_SX` in `ProductsTable.jsx` und `KARTE_TITEL_SX`).
 *
 *  🔴 **An der OBERKANTE verankert (`top: 0`), die 24 px gehen nach UNTEN.**
 *  Die Nachbarflaeche, von der die Flaeche wegwachsen muss, liegt hier OBEN: in
 *  der Produktzelle das Namens-Band 1. Ein nach oben wachsender Boden ueberliefe
 *  den Namens-Link.
 *
 *  🔴 **Der Preis, und er steht hier (R16 W2).** Im Tabellenband (ab `sm`, also
 *  auch auf dem iPad) traegt der Marken-Link danach **24 statt 44 px** auf
 *  grobem Zeiger — die Haus-Regel ist dort nicht gehalten, die Norm schon.
 *  Daneben liegt der **Zeilenklick auf das Produkt**: ein knapp danebengehender
 *  Tipp oeffnet nicht nichts, sondern die **falsche** Seite (Produkt statt
 *  Lieferant). Der Tausch ist bewusst und gemessen, nicht uebersehen.
 *
 *  🔴 **Abgrenzung zu `MICRO_INLINE_BUTTON_SX`** — die zwei sind Gegenstuecke,
 *  nicht Geschwister: jener nimmt einem KNOPF in einer Infozeile den Boden
 *  (`minHeight: 0`, weil eine wachsende Zeile das Layout schoebe, das sie
 *  beschreibt); dieser GIBT einem LINK in einer dichten Zeile den Norm-Boden,
 *  ohne dass die Zeile waechst. Beide sagen „dichte Zeile", und beide ziehen
 *  die entgegengesetzte Folge daraus — der Unterschied ist Knopf gegen Textlink.
 *
 *  ⚠️ **Der Aufrufer muss die Klemme mitziehen.** Sitzt der Link in einem
 *  Traeger mit `overflow: hidden` (`noWrap`), schneidet dessen Padding-Box den
 *  `::after` ab — der Boden waere dann nur so hoch wie die Textzeile. Der
 *  Traeger bekommt `paddingBottom` in Bodenhoehe und ein gleich grosses
 *  negatives `marginBottom` (netto null; die Zeilenhoehe aendert sich nicht).
 *  `ProductsTable.jsx` fuehrt das an `MARKE_BAND_KLEMME_SX` vor.
 *
 *  @useWhen
 *    - ein TEXT-Link in einer dichten Zeile (Tabellenzelle), dessen Trefferflaeche
 *      die WCAG-Untergrenze tragen soll, ohne die Zeile zu heben
 *  @dontUseFor
 *    - ein Knopf in einer Infozeile → `MICRO_INLINE_BUTTON_SX` (dort ist der
 *      Boden ABSICHTLICH aus)
 *    - ein Element, dessen Kasten wachsen DARF → `TOUCH_TARGET_MIN`
 *    - ein Link mit Platz fuer die Haus-Regel → `TOUCH_TARGET_COARSE`
 *
 *  → `sx={{ ...DICHTER_TEXT_LINK_SX }}` */
export const DICHTER_TEXT_LINK_SX = {
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    height: TOUCH_TARGET_MIN.minHeight,
    // 🔴 R16 N2 (2026-10-16): die BREITENACHSE der Norm. Ohne sie ist die
    // Flaeche so breit wie der Link-Kasten — bei einem Inline-Link also wie der
    // TEXT der Marke, und eine 3-zeichenkurze Marke (`ROK`, prod gemessen)
    // faellt damit unter 24 px. `TOUCH_TARGET_MIN` traegt `minWidth: 24`
    // daneben; hier wird sie geholt, nicht zweitgeschrieben.
    minWidth: TOUCH_TARGET_MIN.minWidth,
  },
};
